// ============================================================
// RENDERER
// ============================================================

import {
  BATTLE_COLS, BATTLE_ROWS,
  ELEM, ECOL, SPELLS, UCFG,
} from './constants.ts';
import { CARDS } from './cards/CardData.ts';
import { DECK_TEMPLATES } from './cards/DeckTemplates.ts';
import { LEVELS } from './LevelData.ts';

import { md, clamp } from './utils.ts';
import { ctx, W, H, CB, OYB } from './canvas.ts';
import {
  ISO_TW, ISO_TH, ISO_DEPTH, cellToScreen, screenToCell,
  TOP_BAR_H, HAND_H, TIMELINE_H, LEFT_RAIL_W, RIGHT_PANEL_W,
  FIELD_X, FIELD_Y, FIELD_W, FIELD_H, DEPLOY_PANEL_TOP,
} from './canvas.ts';
import { Game, GS, FX } from './Game.ts';
import { Deployment, DeployedUnit } from './Deployment.ts';
import type { BattleUnit } from './BattleUnit.ts';
import type { TerrainCell, TerrainGrid } from './server/terrain.ts';

export class Renderer {
  draw(g: Game): void {
    ctx.clearRect(0, 0, W, H);
    this._bg();
    // State-specific content first; overlays render on top afterward.
    if (g.state === GS.MENU) {
      this._menu(g);
    } else if (g.state === GS.LOBBY) {
      this._lobby(g);
    } else if (g.state === GS.LEVELS) {
      this._levels(g);
    } else if (g.state === GS.DEPLOY) {
      this._deploy(g);
    } else {
      // Card Tactician — iso battlefield + top HUD + 3-tick timeline +
      // bottom hand row + side rails.
      this._battlefield(g);
      if (g.state === GS.SCHED && g.targeting) this._targetingHint(g);
      if (g.state === GS.SCHED || g.state === GS.EXEC) {
        this._cardHud(g);
        this._cardSidePanels(g);
        this._cardTimeline(g);
        this._cardHand(g);
        this._cardFx(g);
        this._phaseBanner(g);
        if (g.tutorMode) this._cardTutorOverlay(g);
      }
      if (g.state === GS.OVER) this._gameOver(g);
      this._fx(g);
    }
    this._transition(g);
    // Overlays — always considered, never gated by state.
    if (g.waitingForOpponent) this._waitingOverlay(g);
    if (g.initPending) this._loadingOverlay(g);
    // Surrender confirm modal — on top of EVERYTHING.
    if (g.surrenderConfirmActive) this._surrenderConfirm(g);
  }

  // ----------------------------------------------------------
  // SURRENDER confirm modal
  // ----------------------------------------------------------

  /** Hit-test rect for the modal's Confirm button. */
  static surrenderConfirmRect(): { x: number; y: number; w: number; h: number } {
    const w = W * 0.32;
    const h = 40;
    const cy = H / 2 + 26;
    return { x: W / 2 + 8, y: cy, w, h };
  }

  /** Hit-test rect for the modal's Cancel button. */
  static surrenderCancelRect(): { x: number; y: number; w: number; h: number } {
    const w = W * 0.32;
    const h = 40;
    const cy = H / 2 + 26;
    return { x: W / 2 - w - 8, y: cy, w, h };
  }

  /** Modal asking the player to confirm surrender. Two buttons:
   *  - Cancel (gray, left)
   *  - Confirm (red, right) */
  private _surrenderConfirm(g: Game): void {
    // Dim full-screen scrim
    ctx.fillStyle = 'rgba(0,0,0,0.72)';
    ctx.fillRect(0, 0, W, H);

    // Panel
    const panelW = W * 0.84;
    const panelH = 200;
    const px = (W - panelW) / 2;
    const py = (H - panelH) / 2 - 30;
    ctx.fillStyle = '#1a1f2c';
    ctx.fillRect(px, py, panelW, panelH);
    ctx.strokeStyle = '#aa4444';
    ctx.lineWidth = 2;
    ctx.strokeRect(px + 0.5, py + 0.5, panelW - 1, panelH - 1);

    // Title
    ctx.fillStyle = '#ffaa66';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    ctx.fillText('確定投降？', W / 2, py + 22);

    // Subtitle
    ctx.fillStyle = '#bbccdd';
    ctx.font = '13px sans-serif';
    ctx.fillText('對方將直接獲勝', W / 2, py + 56);

    // Buttons
    const confirm = Renderer.surrenderConfirmRect();
    const cancel = Renderer.surrenderCancelRect();
    // Cancel
    ctx.fillStyle = '#2a2e44';
    ctx.fillRect(cancel.x, cancel.y, cancel.w, cancel.h);
    ctx.strokeStyle = '#556677';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(cancel.x + 0.5, cancel.y + 0.5, cancel.w - 1, cancel.h - 1);
    ctx.fillStyle = '#bbccdd';
    ctx.font = 'bold 15px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('取消', cancel.x + cancel.w / 2, cancel.y + cancel.h / 2);
    // Confirm
    ctx.fillStyle = '#5a1a1a';
    ctx.fillRect(confirm.x, confirm.y, confirm.w, confirm.h);
    ctx.strokeStyle = '#cc4444';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(confirm.x + 0.5, confirm.y + 0.5, confirm.w - 1, confirm.h - 1);
    ctx.fillStyle = '#ffaa88';
    ctx.fillText('投降', confirm.x + confirm.w / 2, confirm.y + confirm.h / 2);
  }

  /** Phase 3d Step 4 — semi-transparent banner that says "waiting for opponent". */
  private _waitingOverlay(_g: Game): void {
    const bandH = 38;
    const y = OYB - 4 - bandH;
    ctx.fillStyle = 'rgba(20,30,55,0.88)';
    ctx.fillRect(0, y, W, bandH);
    ctx.strokeStyle = '#ffaa44';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, y + bandH); ctx.lineTo(W, y + bandH); ctx.stroke();
    ctx.fillStyle = '#ffcc66';
    ctx.font = 'bold 15px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    // Animated dots based on time
    const t = (Date.now() / 350) | 0;
    const dots = '.'.repeat((t % 4));
    ctx.fillText('Waiting for opponent' + dots, W / 2, y + bandH / 2);
  }

  private _bg(): void {
    // 深色藍紫漸層 + 微弱中央光暈（魔法氛圍，取代原本純平塗 #080c18）。
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#0e1330');
    g.addColorStop(0.55, '#0a0e1e');
    g.addColorStop(1, '#070a14');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    const rg = ctx.createRadialGradient(W / 2, H * 0.4, 0, W / 2, H * 0.4, W * 0.62);
    rg.addColorStop(0, 'rgba(70,90,180,0.10)');
    rg.addColorStop(1, 'rgba(70,90,180,0)');
    ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);
  }

  // Phase 3d Step 3: menu button geometry — shared with Input.ts hit-testing.
  static readonly MENU_BTN_W = 240;
  static readonly MENU_BTN_H = 44;
  static readonly MENU_BTN_GAP = 14;

  /** Y center of menu button N (0=top). */
  static menuButtonY(idx: number): number {
    return H * 0.5 + idx * (Renderer.MENU_BTN_H + Renderer.MENU_BTN_GAP);
  }

  /** Returns the rectangle of menu button N. */
  static menuButtonRect(idx: number): { x: number; y: number; w: number; h: number } {
    const w = Renderer.MENU_BTN_W;
    const h = Renderer.MENU_BTN_H;
    return { x: (W - w) / 2, y: Renderer.menuButtonY(idx) - h / 2, w, h };
  }

  private _drawMenuButton(idx: number, label: string, color = '#44ccff'): void {
    const r = Renderer.menuButtonRect(idx);
    ctx.fillStyle = 'rgba(20,30,55,0.85)';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    ctx.fillStyle = color;
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label, W / 2, r.y + r.h / 2);
  }

  private _menu(g: Game): void {
    // Title
    ctx.fillStyle = '#ffdd44';
    ctx.font = 'bold 30px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('Card Tactician', W / 2, H * 0.3);
    // Subtitle
    ctx.fillStyle = '#8899bb';
    ctx.font = '14px sans-serif';
    ctx.fillText('卡牌自走戰棋', W / 2, H * 0.3 + 36);

    if (!g.netMode) {
      // Non-net build (e.g. `npm run dev` default): no PvP buttons, so
      // we show the three local modes as explicit equal-rank buttons.
      // Test Mode replaces the legacy "Tap anywhere" Single Player flow —
      // a real button is less confusing for first-time players.
      this._drawMenuButton(0, '單機對戰', '#44ccff');
      return;
    }

    // Net mode: 4 explicit buttons (Test, Create, Join, Server).
    // "Test Mode" is the local-vs-AI sandbox — same code path as the old
    // "Single Player" button, just labelled to reflect what it's for
    // (rapid iteration / dev testing rather than a "real" matchup).
    this._drawMenuButton(0, '單機對戰 (卡牌)', '#888899');
    this._drawMenuButton(1, 'Create Room',   '#44ccff');
    this._drawMenuButton(2, 'Join Room',     '#44ff88');

    // Phase 5a Step 1: Server URL editor button (idx=3).
    // Custom-drawn (two-line) instead of going through _drawMenuButton
    // so we can fit a longer URL with a smaller font.
    this._drawServerButton(g);

    // Net status line — only show when adapter has actually opened.
    const adapter = g.adapter as any;
    const status = adapter?.status ?? 'idle';
    if (status !== 'idle') {
      ctx.fillStyle = '#5566aa';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('connection: ' + status,
        W / 2, H * 0.5 + 4 * (Renderer.MENU_BTN_H + Renderer.MENU_BTN_GAP) - 4);
    }
  }

  /**
   * Phase 5a Step 1: render the "Server URL — tap to edit" button at
   * menu slot idx=3. Two lines: hint label + the current host. Hit-
   * testing in Input.ts uses Renderer.menuButtonRect(3), so the rect
   * stays consistent with the standard button geometry.
   */
  private _drawServerButton(g: Game): void {
    const r = Renderer.menuButtonRect(3);
    // Background + thin border (visually de-emphasised vs the action buttons)
    ctx.fillStyle = 'rgba(20,30,55,0.6)';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = '#556677';
    ctx.lineWidth = 1;
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);

    // Top line: hint
    ctx.fillStyle = '#8899aa';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('Server URL — tap to edit', W / 2, r.y + 12);

    // Bottom line: current URL host (truncated if too long).
    const host = g.netUrl.replace(/^wss?:\/\//, '');
    ctx.fillStyle = '#bbccdd';
    ctx.font = '12px monospace';
    const display = host.length > 30 ? host.slice(0, 14) + '…' + host.slice(-14) : host;
    ctx.fillText(display, W / 2, r.y + r.h - 14);
  }

  private _lobby(g: Game): void {
    // Title
    ctx.fillStyle = '#ffdd44';
    ctx.font = 'bold 26px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('PvP Lobby', W / 2, H * 0.25);

    // Mode line
    ctx.fillStyle = '#8899bb';
    ctx.font = '14px sans-serif';
    const modeLabel = g.gameMode === 'pvp-host' ? 'You are HOST (team 0)'
      : g.gameMode === 'pvp-guest' ? 'You are GUEST (team 1)'
      : '';
    ctx.fillText(modeLabel, W / 2, H * 0.25 + 30);

    // Room code. Big + "share" subtext while still pairing; small text
    // only after both deployments are submitted (no longer needs sharing).
    if (g.roomCode) {
      if (!g.awaitingBattleReady) {
        ctx.fillStyle = '#44ff88';
        ctx.font = 'bold 36px monospace';
        ctx.fillText(g.roomCode, W / 2, H * 0.4);
        ctx.fillStyle = '#5566aa';
        ctx.font = '12px sans-serif';
        ctx.fillText('share this code with your opponent', W / 2, H * 0.4 + 28);
      } else {
        ctx.fillStyle = '#556677';
        ctx.font = '14px monospace';
        ctx.fillText('Room: ' + g.roomCode, W / 2, H * 0.4);
      }
    }

    // Status message — moved up to H*0.55 to leave room for Cancel below.
    ctx.fillStyle = '#cccccc';
    ctx.font = '14px sans-serif';
    ctx.fillText(g.lobbyMessage || '...', W / 2, H * 0.55);

    // Cancel button — moved down to button slot 3 (was 2) so it doesn't
    // collide with the status message text. Input.ts hit-tests the same idx.
    this._drawMenuButton(3, 'Cancel', '#ff8866');
  }

  // ----------------------------------------------------------
  // (Story / Casual pickers + tutorial overlays removed in M6b)
  // ----------------------------------------------------------

  /** 關卡選擇卡片矩形（垂直列表；命中與繪製同源）。 */
  static levelButtonRect(i: number): { x: number; y: number; w: number; h: number } {
    const cardW = 700, cardH = 66, gap = 14;
    const total = LEVELS.length * cardH + (LEVELS.length - 1) * gap;
    const startY = Math.round((H - total) / 2) + 20;
    return { x: (W - cardW) / 2, y: startY + i * (cardH + gap), w: cardW, h: cardH };
  }
  /** 關卡選擇「主選單」返回鍵。 */
  static levelBackRect(): { x: number; y: number; w: number; h: number } {
    return { x: (W - 200) / 2, y: H - 56, w: 200, h: 38 };
  }

  /** 單機關卡選擇畫面（GS.LEVELS）。 */
  private _levels(g: Game): void {
    ctx.fillStyle = '#ffd24a'; ctx.font = 'bold 30px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('選擇關卡', W / 2, 70);
    ctx.fillStyle = '#8aa0c0'; ctx.font = '14px sans-serif';
    ctx.fillText('選定後：AI 牌組與雙方單位/位置固定；你只需選自己的牌組並規劃路徑', W / 2, 104);
    for (let i = 0; i < LEVELS.length; i++) {
      const lv = LEVELS[i];
      const r = Renderer.levelButtonRect(i);
      ctx.fillStyle = '#141d33'; this._rrPath(r.x, r.y, r.w, r.h, 10); ctx.fill();
      ctx.strokeStyle = '#33507a'; ctx.lineWidth = 1.5; this._rrPath(r.x, r.y, r.w, r.h, 10); ctx.stroke();
      ctx.fillStyle = '#dfe8f6'; ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillText(lv.name, r.x + 20, r.y + 22);
      ctx.fillStyle = '#8aa0c0'; ctx.font = '13px sans-serif';
      ctx.fillText(lv.desc, r.x + 20, r.y + 46);
    }
    const b = Renderer.levelBackRect();
    ctx.fillStyle = 'rgba(20,30,55,0.6)'; this._rrPath(b.x, b.y, b.w, b.h, 8); ctx.fill();
    ctx.strokeStyle = '#556677'; ctx.lineWidth = 1; this._rrPath(b.x, b.y, b.w, b.h, 8); ctx.stroke();
    ctx.fillStyle = '#8899aa'; ctx.font = '13px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('← 主選單', b.x + b.w / 2, b.y + b.h / 2);
    ctx.textBaseline = 'alphabetic';
  }

  /** 關卡制佈陣路徑規劃引導 chip（戰場中央上方）。 */
  private _deployPathHint(g: Game, dep: Deployment): void {
    const t = performance.now() / 1000;
    const pulse = 0.5 + 0.5 * Math.sin(t * 3);
    const noPath = dep.placed.filter((u) => u.path.length === 0).length;
    const sel = dep.selectedUnit >= 0;
    const label = sel
      ? '在戰場相鄰格點擊 / 拖曳鋪路徑 · 再點該單位可重畫'
      : `點我方單位規劃移動路徑${noPath > 0 ? `（還有 ${noPath} 個未設）` : '（可再調整）'}`;
    const cx = FIELD_X + FIELD_W / 2, y = FIELD_Y + 22;
    ctx.font = 'bold 15px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const w = ctx.measureText(label).width + 28;
    ctx.globalAlpha = 0.9 + 0.1 * pulse;
    ctx.fillStyle = 'rgba(18,36,64,0.92)'; this._rrPath(cx - w / 2, y - 15, w, 30, 8); ctx.fill();
    ctx.strokeStyle = `rgba(130,205,255,${(0.55 + 0.4 * pulse).toFixed(3)})`; ctx.lineWidth = 2;
    this._rrPath(cx - w / 2, y - 15, w, 30, 8); ctx.stroke();
    ctx.fillStyle = '#e8f4ff'; ctx.fillText(label, cx, y);
    ctx.globalAlpha = 1; ctx.textBaseline = 'alphabetic';
  }

  private _deploy(g: Game): void {
    const dep = g.deployment;

    // Draw battlefield grid (top portion)
    this._deployGrid(g, dep);

    // 關卡制路徑規劃引導（preset）：中央提示 chip。
    if (dep.rules.kind === 'preset') this._deployPathHint(g, dep);

    // Draw bottom panel (palette / buttons)
    this._deployPanel(g, dep);

    // Draw drag ghost (palette → grid)
    if (dep.dragging && dep.dragPos && dep.dragType) {
      const cfg = UCFG[dep.dragType];
      const px = dep.dragPos.x, py = dep.dragPos.y;
      const r = CB * 0.36;
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = '#4488ff';
      this._drawUnitShape(cfg.shape, px, py, r);
      ctx.globalAlpha = 1;
    }

    // Draw place-mode grid drag ghost (reposition or remove). 落點是否合法的
    // 高亮已由 _deployGrid 的 iso 落點高亮處理；這裡只畫跟著游標的單位殘影。
    if (dep.dragRemoveIdx >= 0 && dep.dragPos) {
      const u = dep.placed[dep.dragRemoveIdx];
      if (u) {
        const cfg = UCFG[u.type];
        const px = dep.dragPos.x, py = dep.dragPos.y;
        const r = CB * 0.36;
        const inRemoveZone = py >= DEPLOY_PANEL_TOP;
        ctx.globalAlpha = inRemoveZone ? 0.45 : 0.8;
        ctx.fillStyle = inRemoveZone ? '#ff4455' : '#4488ff';
        this._drawUnitShape(cfg.shape, px, py, r);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 13px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(u.type[0], px, py);
        ctx.globalAlpha = 1;
        // 拖到底部面板 = 移除提示
        if (inRemoveZone) {
          ctx.fillStyle = 'rgba(255,68,85,0.3)';
          ctx.fillRect(0, DEPLOY_PANEL_TOP, W, H - DEPLOY_PANEL_TOP);
          ctx.fillStyle = '#ff6666'; ctx.font = 'bold 14px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('放開以移除', W / 2, DEPLOY_PANEL_TOP + (H - DEPLOY_PANEL_TOP) / 2);
        }
        ctx.textBaseline = 'alphabetic';
      }
    }
  }

  /** Draw the 8x8 grid during deployment */
  private _deployGrid(g: Game, dep: Deployment): void {
    // ── iso 立體格佈陣盤（與戰鬥同一塊 45° 板）──────────────────
    const TWH = ISO_TW / 2, THH = ISO_TH / 2;
    const UNIT_LIFT = ISO_DEPTH + 4;
    const tcolors = (cell: TerrainCell | 'none'): { top: string; l: string; r: string; lift: number } => {
      switch (cell) {
        case 'grass':        return { top: '#4e9044', l: '#356a30', r: '#2c5a28', lift: 0 };
        case 'burningGrass': return { top: '#7a3a26', l: '#4a2014', r: '#3a1810', lift: 0 };
        case 'puddle':       return { top: '#2f6aa0', l: '#1f4a72', r: '#193c5c', lift: 0 };
        case 'swamp':        return { top: '#5a6238', l: '#3e4626', r: '#32381e', lift: 0 };
        case 'boulder':      return { top: '#8a8a92', l: '#5f5f66', r: '#4a4a50', lift: 10 };
        default:             return { top: '#3a4a63', l: '#26344a', r: '#1e2a3c', lift: 0 };
      }
    };
    const topPath = (sx: number, sy: number) => {
      ctx.beginPath();
      ctx.moveTo(sx, sy - THH); ctx.lineTo(sx + TWH, sy);
      ctx.lineTo(sx, sy + THH); ctx.lineTo(sx - TWH, sy); ctx.closePath();
    };
    // 地形 tile（back-to-front）
    for (let s = 0; s <= (BATTLE_COLS - 1) + (BATTLE_ROWS - 1); s++) {
      for (let cx = 0; cx < BATTLE_COLS; cx++) {
        const cy = s - cx;
        if (cy < 0 || cy >= BATTLE_ROWS) continue;
        const c = cellToScreen(cx, cy);
        const cell: TerrainCell | 'none' = g.terrain ? (g.terrain[cy]?.[cx] ?? 'none') : 'none';
        const col = tcolors(cell);
        const topY = c.y - col.lift;
        const depth = ISO_DEPTH + (col.lift > 0 ? col.lift : 0);
        const right = { x: c.x + TWH, y: topY }, bot = { x: c.x, y: topY + THH }, left = { x: c.x - TWH, y: topY };
        ctx.fillStyle = col.l;
        ctx.beginPath(); ctx.moveTo(left.x, left.y); ctx.lineTo(bot.x, bot.y);
        ctx.lineTo(bot.x, bot.y + depth); ctx.lineTo(left.x, left.y + depth); ctx.closePath(); ctx.fill();
        ctx.fillStyle = col.r;
        ctx.beginPath(); ctx.moveTo(bot.x, bot.y); ctx.lineTo(right.x, right.y);
        ctx.lineTo(right.x, right.y + depth); ctx.lineTo(bot.x, bot.y + depth); ctx.closePath(); ctx.fill();
        // 頂面：漸層 + 每型別紋理（與戰場 _isoTopFill 共用）。
        this._isoTopFill(c.x, topY, cell, col.top, null);
      }
    }
    // 玩家可放置區（view rows 6-7）高亮
    for (let r = 6; r <= 7; r++) {
      for (let c = 0; c < BATTLE_COLS; c++) {
        if (!dep.isPlayerCell(c, r)) continue;
        const s = cellToScreen(c, r);
        ctx.fillStyle = 'rgba(68,136,255,0.18)'; topPath(s.x, s.y); ctx.fill();
      }
    }
    // 拖曳落點高亮（移動已放單位 or 從 palette 拖放）：綠＝可放、紅＝不可放。
    const dragActive = (dep.dragRemoveIdx >= 0 || (dep.dragging && !!dep.dragType)) && !!dep.dragPos;
    if (dragActive && dep.dragPos) {
      const { cx, cy } = screenToCell(dep.dragPos.x, dep.dragPos.y);
      if (cx >= 0 && cx < BATTLE_COLS && cy >= 0 && cy < BATTLE_ROWS) {
        const dragged = dep.dragRemoveIdx >= 0 ? dep.placed[dep.dragRemoveIdx] : null;
        const ownCell = !!dragged && dragged.pos.x === cx && dragged.pos.y === cy;
        const valid = dep.isPlayerCell(cx, cy) && (!dep.isOccupied(cx, cy) || ownCell);
        const s = cellToScreen(cx, cy);
        ctx.fillStyle = valid ? 'rgba(90,235,130,0.45)' : 'rgba(235,90,90,0.4)';
        topPath(s.x, s.y); ctx.fill();
        ctx.strokeStyle = valid ? '#7cff9a' : '#ff6a6a'; ctx.lineWidth = 3;
        topPath(s.x, s.y); ctx.stroke();
      }
    }
    // 基地 tiles（world→view）
    const baseTiles = [
      { x: 3, y: 7, team: 0 }, { x: 4, y: 7, team: 0 },
      { x: 3, y: 0, team: 1 }, { x: 4, y: 0, team: 1 },
    ];
    for (const bt of baseTiles) {
      const teamCol = bt.team === g.myTeam ? '68,136,255' : '255,68,85';
      const s = cellToScreen(g.viewFlipX(bt.x), g.viewFlipY(bt.y));
      ctx.fillStyle = `rgba(${teamCol},0.3)`; topPath(s.x, s.y); ctx.fill();
      ctx.strokeStyle = `rgba(${teamCol},0.85)`; ctx.lineWidth = 2; topPath(s.x, s.y); ctx.stroke();
      const sz = ISO_TH * 0.32;
      ctx.fillStyle = `rgba(${teamCol},0.9)`;
      ctx.fillRect(s.x - sz, s.y - sz * 1.1, sz * 2, sz * 1.3);
      ctx.fillRect(s.x - sz * 1.25, s.y - sz * 1.7, sz * 0.5, sz * 0.8);
      ctx.fillRect(s.x + sz * 0.75, s.y - sz * 1.7, sz * 0.5, sz * 0.8);
    }
    // 已放置單位（back-to-front）
    const order = dep.placed
      .map((u, i) => ({ u, i }))
      .filter((e) => e.i !== dep.dragRemoveIdx)
      .sort((a, b) => (a.u.pos.x + a.u.pos.y) - (b.u.pos.x + b.u.pos.y));
    for (const { u, i } of order) {
      const cfg = UCFG[u.type];
      const c = cellToScreen(u.pos.x, u.pos.y);
      const px = c.x, py = c.y - UNIT_LIFT, r = ISO_TW * 0.22;
      const isSelected = i === dep.selectedUnit;
      if (isSelected) {
        ctx.strokeStyle = '#ffdd44'; ctx.lineWidth = 2; topPath(c.x, c.y); ctx.stroke();
      }
      // 關卡制：未設路徑的己方單位脈動高亮 + 「!」提示，引導玩家規劃。
      if (dep.rules.kind === 'preset' && u.path.length === 0 && !isSelected) {
        const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 240 + i);
        ctx.strokeStyle = `rgba(120,230,140,${(0.35 + 0.5 * pulse).toFixed(3)})`;
        ctx.lineWidth = 2.5; topPath(c.x, c.y); ctx.stroke();
        ctx.fillStyle = `rgba(150,240,160,${(0.6 + 0.4 * pulse).toFixed(3)})`;
        ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('!', px + r * 0.9, py - r * 0.9);
      }
      if (u.path.length > 0) {
        // 深色底線墊高對比（避免虛線與深底色糊在一起），再畫亮色虛線。
        ctx.strokeStyle = 'rgba(8,12,22,0.55)'; ctx.lineWidth = 4; ctx.setLineDash([]);
        ctx.beginPath(); ctx.moveTo(c.x, c.y);
        for (const p of u.path) { const pp = cellToScreen(p.x, p.y); ctx.lineTo(pp.x, pp.y); }
        ctx.stroke();
        ctx.strokeStyle = isSelected ? 'rgba(255,221,68,0.95)' : 'rgba(140,200,255,0.8)';
        ctx.lineWidth = 2.5; ctx.setLineDash([5, 4]);
        ctx.beginPath(); ctx.moveTo(c.x, c.y);
        for (const p of u.path) { const pp = cellToScreen(p.x, p.y); ctx.lineTo(pp.x, pp.y); }
        ctx.stroke(); ctx.setLineDash([]);
        const last = cellToScreen(u.path[u.path.length - 1].x, u.path[u.path.length - 1].y);
        ctx.fillStyle = isSelected ? 'rgba(255,221,68,0.9)' : 'rgba(160,210,255,0.9)';
        ctx.beginPath(); ctx.arc(last.x, last.y, 5, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = 'rgba(8,12,22,0.6)'; ctx.lineWidth = 1; ctx.stroke();
      }
      ctx.fillStyle = '#4488ff';
      this._drawUnitShape(cfg.shape, px, py, r);
      if (isSelected && u.path.length > 0) {
        ctx.fillStyle = '#ffdd44'; ctx.font = '11px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(`${u.path.length}/${cfg.maxPath}`, px, py + r + 9);
      }
    }
    // 頂列標題帶
    const tbH = 30;
    ctx.fillStyle = '#0e1328'; ctx.fillRect(0, 0, W, tbH);
    ctx.font = 'bold 14px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const selU = dep.selectedUnit >= 0 ? dep.placed[dep.selectedUnit] : null;
    let title: string;
    if (dep.pathMode) {
      ctx.fillStyle = '#88ffbb';
      title = selU
        ? `路徑模式 · ${selU.type} ${selU.path.length}/${UCFG[selU.type].maxPath}（再點一次重畫）`
        : '路徑模式 · 點一個單位規劃路徑';
    } else {
      ctx.fillStyle = '#ffdd44';
      title = selU ? `${selU.type} 已選 · 拖曳移動、往下拖移除` : '佈陣 · 把單位拖到藍色區';
    }
    ctx.fillText(title, W / 2, tbH / 2);
    if (g.deployDeadline) {
      const remain = Math.max(0, Math.ceil((g.deployDeadline - Date.now()) / 1000));
      ctx.fillStyle = remain <= 10 ? '#ff4444' : '#44ff88';
      ctx.font = 'bold 16px monospace'; ctx.textAlign = 'right';
      ctx.fillText(`${remain}s`, W - 10, tbH / 2);
    }
    ctx.textBaseline = 'alphabetic';
  }

  /** Draw deployment bottom panel.
   *
   *  Branches on `dep.rules.kind` (single source of truth for UI mode):
   *   - 'manual' (single / PvP): full panel — palette + Draw Paths toggle
   *      + Start + save/load slots + Remove + Main Menu back button.
   *   - 'preset' (Story / Casual): simplified panel — Path Mode toggle +
   *      Start + Back button. Units are pre-placed by the script so the
   *      placement-related UI is hidden entirely.
   */
  /** 關卡制佈陣底部面板（preset）：我方牌組選擇 + Start + 返回關卡選擇。
   *  ⚠️ 座標須與 Input._getDeployButtonsPreset 一致（命中同源）。 */
  private _deployPanelPreset(g: Game, dep: Deployment): void {
    const T = DEPLOY_PANEL_TOP;
    ctx.fillStyle = '#0e1328'; ctx.fillRect(0, T, W, H - T);
    ctx.strokeStyle = '#1a2040'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, T); ctx.lineTo(W, T); ctx.stroke();
    // 我方牌組選擇列
    const items = DECK_TEMPLATES.map((d) => ({ id: d.id, label: d.name }));
    const ldrGap = 6;
    const ldrW = (W * 0.92 - ldrGap * (items.length - 1)) / items.length;
    const leaderY = T + 34, leaderH = 30;
    ctx.fillStyle = '#8899aa'; ctx.font = '12px sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText('我方牌組', W * 0.04, T + 16);
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const lx = W * 0.04 + i * (ldrW + ldrGap);
      const sel = g.playerDeckId === it.id;
      ctx.fillStyle = sel ? '#1d2a48' : '#0e1828'; ctx.fillRect(lx, leaderY, ldrW, leaderH);
      ctx.strokeStyle = sel ? '#ffd24a' : '#335577'; ctx.lineWidth = sel ? 2 : 1;
      ctx.strokeRect(lx, leaderY, ldrW, leaderH);
      ctx.fillStyle = sel ? '#ffd24a' : '#aaccee'; ctx.font = sel ? 'bold 13px sans-serif' : '13px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(it.label, lx + ldrW / 2, leaderY + leaderH / 2 + 1);
    }
    // Start Battle
    const stY = leaderY + leaderH + 14, stH = 44;
    const canStart = dep.canConfirm();
    ctx.fillStyle = canStart ? '#1a3520' : 'rgba(100,140,220,0.12)'; ctx.fillRect(W * 0.04, stY, W * 0.92, stH);
    ctx.strokeStyle = canStart ? '#44ff88' : 'rgba(100,140,220,0.25)'; ctx.lineWidth = canStart ? 2 : 1;
    ctx.strokeRect(W * 0.04, stY, W * 0.92, stH);
    ctx.fillStyle = canStart ? '#7dff9f' : 'rgba(170,190,220,0.6)'; ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('Start Battle! ▶', W / 2, stY + stH / 2 + 1);
    // Back to level select
    const bkY = stY + stH + 12, bkH = 30;
    ctx.fillStyle = 'rgba(20,30,55,0.6)'; ctx.fillRect(W * 0.30, bkY, W * 0.40, bkH);
    ctx.strokeStyle = '#556677'; ctx.lineWidth = 1; ctx.strokeRect(W * 0.30, bkY, W * 0.40, bkH);
    ctx.fillStyle = '#8899aa'; ctx.font = '13px sans-serif';
    ctx.fillText('← 關卡選擇', W * 0.30 + W * 0.20, bkY + bkH / 2 + 1);
    ctx.textBaseline = 'alphabetic';
  }

  private _deployPanel(g: Game, dep: Deployment): void {
    // 關卡制（preset）：極簡面板——我方牌組 + 開戰 + 返回關卡選擇。
    if (dep.rules.kind === 'preset') { this._deployPanelPreset(g, dep); return; }
    // DEPLOY 面板錨在 DEPLOY_PANEL_TOP（比 ZMY 高），避免諸列爆版切到按鈕。
    // ⚠️ 所有 Y 座標須與 Input._getDeployButtons + 佈陣路由一致（命中同源）。
    const T = DEPLOY_PANEL_TOP;
    // Background (shared by both layouts)
    ctx.fillStyle = '#0e1328';
    ctx.fillRect(0, T, W, H - T);
    ctx.strokeStyle = '#1a2040'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, T); ctx.lineTo(W, T); ctx.stroke();

    // ----- Manual mode panel (single / PvP) -----
    // Palette always visible; path info is surfaced in the top title bar.

    // Palette slots
    const palY = T + 6;
    const palH = 42;
    const palX = 10;
    const palW = (W - 20) / dep.palette.length;

    for (let i = 0; i < dep.palette.length; i++) {
      const slot = dep.palette[i];
      const sx = palX + i * palW;
      const cfg = UCFG[slot.type];
      const remaining = slot.max - slot.placed;
      const available = remaining > 0;

      ctx.fillStyle = available ? '#1a2545' : '#111830';
      ctx.fillRect(sx + 2, palY, palW - 4, palH);
      ctx.strokeStyle = i === dep.selectedPalette ? '#ffdd44' : (available ? '#335588' : '#222');
      ctx.lineWidth = i === dep.selectedPalette ? 2 : 1;
      ctx.strokeRect(sx + 2, palY, palW - 4, palH);

      const ux = sx + palW / 2, uy = palY + 16;
      const r = 11;
      ctx.fillStyle = available ? '#4488ff' : '#333';
      this._drawUnitShape(cfg.shape, ux, uy, r);
      // Session 7: drop type-initial overlay — new thematic icon already
      // conveys unit type; the 3-letter label below repeats it for newbies.

      // Session 8: drop the 3-letter type label (Inf/Cav/Arc) — overlapped
      // with the count below it. The thematic icon already conveys type;
      // count alone is enough.
      // Session 9: shrink the count font (was palW*0.13 ≈ 26px on desktop,
      // way too dominant). Keep a sane mobile minimum.
      ctx.fillStyle = available ? '#aabbdd' : '#555';
      ctx.font = `bold ${Math.max(10, Math.min(13, palW * 0.10))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`${remaining}/${slot.max}`, sx + palW / 2, palY + 34);
    }

    // --- Row L: 牌組選擇器 — 一格一套 DECK_TEMPLATES，選中高亮。單機多畫一行
    //     「敵方牌組」；PvP 隱藏（對手牌組 server 端決定）。下方 Row A 的 baseY
    //     依 showAiPicker 偏移，須與 Input._getDeployButtons 同步。
    const leaderY = T + 52;
    const leaderH = 19;
    const items = DECK_TEMPLATES.map((d) => ({ id: d.id, label: d.name }));
    const ldrGap = 4;
    const ldrW = (W - W * 0.08 - ldrGap * (items.length - 1)) / items.length;
    const showAiPicker = g.gameMode === 'single';
    const aiLeaderY = leaderY + leaderH + 3;

    const drawLeaderRow = (rowY: number, currentId: string, label: string, isAi: boolean) => {
      ctx.fillStyle = '#8899aa'; ctx.font = '10px sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillText(label, W * 0.04, rowY - 7);
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        const lx = W * 0.04 + i * (ldrW + ldrGap);
        const selected = currentId === it.id;
        ctx.fillStyle = selected ? (isAi ? '#2a1d3a' : '#1d2a48') : '#0e1828';
        ctx.fillRect(lx, rowY, ldrW, leaderH);
        ctx.strokeStyle = selected ? (isAi ? '#cc88ff' : '#ffd24a') : '#335577';
        ctx.lineWidth = selected ? 2 : 1;
        ctx.strokeRect(lx, rowY, ldrW, leaderH);
        ctx.fillStyle = selected ? (isAi ? '#cc88ff' : '#ffd24a') : '#aaccee';
        ctx.font = selected ? 'bold 12px sans-serif' : '12px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(it.label, lx + ldrW / 2, rowY + leaderH / 2 + 1);
      }
    };

    drawLeaderRow(leaderY, g.playerDeckId, '我方牌組', false);
    if (showAiPicker) drawLeaderRow(aiLeaderY, g.aiDeckId, '敵方牌組', true);

    // --- Row A: [Draw Paths toggle (40%)] [Confirm / placeholder (60%)] ---
    // 單機多一行牌組選擇 → Row A 再下移；數字須與 Input._getDeployButtons 一致。
    const btnY = showAiPicker ? T + 98 : T + 74;
    const btnH = 30;
    const dpW = W * 0.36;
    const cfX = W * 0.04 + dpW + 8;
    const cfW = W - cfX - W * 0.04;

    // Draw Paths toggle
    const pmOn = dep.pathMode;
    ctx.fillStyle = pmOn ? '#15301f' : '#0e1828';
    ctx.fillRect(W * 0.04, btnY, dpW, btnH);
    ctx.strokeStyle = pmOn ? '#88ffbb' : '#3a5577';
    ctx.lineWidth = pmOn ? 2 : 1;
    ctx.strokeRect(W * 0.04, btnY, dpW, btnH);
    ctx.fillStyle = pmOn ? '#aaffcc' : '#88aacc';
    ctx.font = pmOn ? 'bold 13px sans-serif' : '13px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(pmOn ? '✏ Drawing Paths' : 'Draw Paths', W * 0.04 + dpW / 2, btnY + btnH / 2 + 1);

    // Confirm / Start Battle (or placeholder when nothing placed)
    if (dep.canConfirm()) {
      ctx.fillStyle = '#1a3520'; ctx.fillRect(cfX, btnY, cfW, btnH);
      ctx.strokeStyle = '#44ff88'; ctx.lineWidth = 1.5; ctx.strokeRect(cfX, btnY, cfW, btnH);
      ctx.fillStyle = '#44ff88'; ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Start Battle!', cfX + cfW / 2, btnY + btnH / 2 + 1);
    } else {
      ctx.fillStyle = 'rgba(100,140,220,0.18)'; ctx.fillRect(cfX, btnY, cfW, btnH);
      ctx.strokeStyle = 'rgba(100,140,220,0.25)'; ctx.lineWidth = 1; ctx.strokeRect(cfX, btnY, cfW, btnH);
      ctx.fillStyle = 'rgba(170,190,220,0.6)'; ctx.font = '12px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Place a unit to start', cfX + cfW / 2, btnY + btnH / 2 + 1);
    }

    // --- Row B: Save/Load slot row (3 tiles) ---
    const sSlotY = btnY + btnH + 5;
    const sSlotH = 22;
    const sSlotGap = 4;
    const sSlotW = (W * 0.92 - sSlotGap * 2) / 3;
    const slots = g.listSlots();
    const action = g.slotAction;

    for (let i = 0; i < 3; i++) {
      const sx = W * 0.04 + i * (sSlotW + sSlotGap);
      const data = slots[i];
      const filled = data !== null;
      let bg = '#101830', border = '#2a3858', fg = '#556688';
      if (filled) { bg = '#132035'; border = '#4488bb'; fg = '#aaccee'; }
      if (action === 'save')  { border = '#44ff88'; bg = '#0e2015'; }
      if (action === 'clear') { border = '#ff6644'; bg = '#200e0e'; }
      ctx.fillStyle = bg; ctx.fillRect(sx, sSlotY, sSlotW, sSlotH);
      ctx.strokeStyle = border; ctx.lineWidth = 1.2;
      ctx.strokeRect(sx, sSlotY, sSlotW, sSlotH);
      ctx.fillStyle = fg;
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const label = filled
        ? `Slot ${i + 1}  ·  ${data!.units.length}u`
        : `Slot ${i + 1}  ·  —`;
      ctx.fillText(label, sx + sSlotW / 2, sSlotY + sSlotH / 2 + 1);
    }

    // --- Row C: Save / Clear toggle ---
    const actY = sSlotY + sSlotH + 5;
    const actH = 22;
    const actW = W * 0.44;

    const saveOn = action === 'save';
    ctx.fillStyle = saveOn ? '#14301c' : '#0e1828';
    ctx.fillRect(W * 0.04, actY, actW, actH);
    ctx.strokeStyle = saveOn ? '#44ff88' : '#335566'; ctx.lineWidth = saveOn ? 2 : 1;
    ctx.strokeRect(W * 0.04, actY, actW, actH);
    ctx.fillStyle = saveOn ? '#88ffbb' : '#88bbdd';
    ctx.font = saveOn ? 'bold 12px sans-serif' : '12px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(saveOn ? 'Tap slot to SAVE' : '↓ Save to slot', W * 0.04 + actW / 2, actY + actH / 2 + 1);

    const clrOn = action === 'clear';
    ctx.fillStyle = clrOn ? '#2a1016' : '#0e1828';
    ctx.fillRect(W * 0.52, actY, actW, actH);
    ctx.strokeStyle = clrOn ? '#ff6644' : '#335566'; ctx.lineWidth = clrOn ? 2 : 1;
    ctx.strokeRect(W * 0.52, actY, actW, actH);
    ctx.fillStyle = clrOn ? '#ffaa88' : '#bb7788';
    ctx.font = clrOn ? 'bold 12px sans-serif' : '12px sans-serif';
    ctx.fillText(clrOn ? 'Tap slot to CLEAR' : '🗑 Clear slot', W * 0.52 + actW / 2, actY + actH / 2 + 1);

    // --- Bottom row: 選中單位時顯示「移除」，否則（單機）顯示「主選單」。
    //     兩者互斥共用同一列，面板高度恆定、不會因選取而多長一列爆版。 ---
    const bottomY = actY + actH + 5;
    const bottomH = 26;
    if (dep.selectedUnit >= 0) {
      ctx.fillStyle = '#2a1520'; ctx.fillRect(W * 0.04, bottomY, W * 0.92, bottomH);
      ctx.strokeStyle = '#ff4455'; ctx.lineWidth = 1; ctx.strokeRect(W * 0.04, bottomY, W * 0.92, bottomH);
      ctx.fillStyle = '#ff6666'; ctx.font = '13px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const su = dep.placed[dep.selectedUnit];
      ctx.fillText(`✕ 移除 ${su.type}`, W / 2, bottomY + bottomH / 2 + 1);
    } else if (g.gameMode === 'single') {
      // PvP 有 LOBBY 可取消；單機 MENU→DEPLOY 直達，需要返回鍵。
      ctx.fillStyle = 'rgba(20,30,55,0.6)'; ctx.fillRect(W * 0.25, bottomY, W * 0.50, bottomH);
      ctx.strokeStyle = '#556677'; ctx.lineWidth = 1; ctx.strokeRect(W * 0.25, bottomY, W * 0.50, bottomH);
      ctx.fillStyle = '#8899aa'; ctx.font = '12px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('← 主選單', W / 2, bottomY + bottomH / 2 + 1);
    }

    // Bottom hint — depends on mode + selection
    ctx.fillStyle = 'rgba(100,140,220,0.4)'; ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    let hint: string;
    if (dep.pathMode) {
      hint = dep.selectedUnit >= 0
        ? 'Drag from unit to draw path · tap unit again to reset'
        : 'Tap a placed unit to start drawing its path';
    } else {
      hint = dep.selectedUnit >= 0
        ? 'Drag unit to a new cell · drag down to remove'
        : 'Drag palette onto blue zone · drag placed units to move/remove';
    }
    ctx.fillText(hint, W / 2, H - 12);
  }

  /**
   * Draw a unit-type icon at (px,py). Session 7 (2026-05-06): replaced
   * the old generic primitives (square/diamond/triangle/circle) with
   * thematic icons drawn in the same rect+arc primitive style as the
   * base castle icon — sword (Infantry), horseshoe (Cavalry), bow
   * (Archer), wand (Mage, kept for re-enable).
   *
   * Caller contract: set `ctx.fillStyle` to the team color before
   * calling. The function uses fillStyle as the icon color and also
   * borrows it for stroke strokes via ctx.strokeStyle = fillStyle.
   * Each branch saves/restores ctx state when it modifies lineWidth /
   * transform so callers don't leak state.
   */
  private _drawUnitShape(shape: string, px: number, py: number, r: number): void {
    const col = ctx.fillStyle as string;
    ctx.save();
    ctx.strokeStyle = col;

    if (shape === 'sword') {
      // Blade: thin vertical bar, top half of icon
      const bw = r * 0.30, bh = r * 1.30;
      ctx.fillRect(px - bw / 2, py - r * 0.85, bw, bh);
      // Cross-guard
      const gw = r * 1.10, gh = r * 0.20;
      ctx.fillRect(px - gw / 2, py + r * 0.30, gw, gh);
      // Grip
      const hw = r * 0.26, hh = r * 0.40;
      ctx.fillRect(px - hw / 2, py + r * 0.50, hw, hh);
      // Pommel
      const pw = r * 0.42, ph = r * 0.18;
      ctx.fillRect(px - pw / 2, py + r * 0.92, pw, ph);
    } else if (shape === 'horseshoe') {
      // Realistic Horseshoe (U-shape wider in the middle, slightly narrower at ends)
      ctx.lineWidth = Math.max(3, r * 0.35);
      ctx.lineCap = 'round';
      ctx.beginPath();
      // Left tip
      ctx.moveTo(px - r * 0.5, py + r * 0.8);
      // Curve up to top
      ctx.bezierCurveTo(
        px - r * 1.0, py - r * 0.1, 
        px - r * 0.6, py - r * 0.9, 
        px, py - r * 0.9
      );
      // Curve down to right tip
      ctx.bezierCurveTo(
        px + r * 0.6, py - r * 0.9, 
        px + r * 1.0, py - r * 0.1, 
        px + r * 0.5, py + r * 0.8
      );
      ctx.stroke();

      // Calkins (small blocks at the ends of the horseshoe)
      ctx.fillRect(px - r * 0.65, py + r * 0.7, r * 0.3, r * 0.2);
      ctx.fillRect(px + r * 0.35, py + r * 0.7, r * 0.3, r * 0.2);

      // Removed nail holes per user request
    } else if (shape === 'bow') {
      // Recurve Bow and drawn arrow
      // Bow Body
      ctx.lineWidth = Math.max(2, r * 0.15);
      ctx.lineCap = 'round';
      ctx.beginPath();
      // Top tip
      ctx.moveTo(px - r * 0.3, py - r * 0.9);
      // Curve to handle
      ctx.bezierCurveTo(
        px + r * 0.4, py - r * 0.8,
        px + r * 0.6, py - r * 0.3,
        px + r * 0.5, py
      );
      // Curve to bottom tip
      ctx.bezierCurveTo(
        px + r * 0.6, py + r * 0.3,
        px + r * 0.4, py + r * 0.8,
        px - r * 0.3, py + r * 0.9
      );
      ctx.stroke();


      // Bowstring (pulled back)
      ctx.lineWidth = Math.max(1, r * 0.05);
      ctx.beginPath();
      ctx.moveTo(px - r * 0.3, py - r * 0.9);
      ctx.lineTo(px - r * 0.7, py); // drawn to left
      ctx.lineTo(px - r * 0.3, py + r * 0.9);
      ctx.stroke();

      // Arrow Shaft
      ctx.lineWidth = Math.max(1.5, r * 0.12);
      ctx.beginPath();
      ctx.moveTo(px - r * 0.8, py); // Nock on string
      ctx.lineTo(px + r * 0.8, py); // Shaft front
      ctx.stroke();

      // Arrowhead (Smaller)
      ctx.beginPath();
      ctx.moveTo(px + r * 0.95, py);
      ctx.lineTo(px + r * 0.75, py - r * 0.15);
      ctx.lineTo(px + r * 0.75, py + r * 0.15);
      ctx.closePath();
      ctx.fill();

      // Fletching (feathers at the back)
      ctx.lineWidth = Math.max(1, r * 0.08);
      ctx.beginPath();
      ctx.moveTo(px - r * 0.7, py);
      ctx.lineTo(px - r * 0.9, py - r * 0.2);
      ctx.moveTo(px - r * 0.6, py);
      ctx.lineTo(px - r * 0.8, py - r * 0.2);
      ctx.moveTo(px - r * 0.7, py);
      ctx.lineTo(px - r * 0.9, py + r * 0.2);
      ctx.moveTo(px - r * 0.6, py);
      ctx.lineTo(px - r * 0.8, py + r * 0.2);
      ctx.stroke();
    } else if (shape === 'wand') {
      // Diagonal wand body, bottom-left → top-right
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(-Math.PI / 4);
      const ww = r * 0.22, wh = r * 1.50;
      ctx.fillRect(-ww / 2, -wh / 2 + r * 0.20, ww, wh);
      ctx.restore();
      // 4-point star at the tip (top-right)
      const sx = px + r * 0.58, sy = py - r * 0.58;
      const ss = r * 0.34;
      ctx.beginPath();
      ctx.moveTo(sx, sy - ss);
      ctx.lineTo(sx + ss * 0.30, sy - ss * 0.30);
      ctx.lineTo(sx + ss, sy);
      ctx.lineTo(sx + ss * 0.30, sy + ss * 0.30);
      ctx.lineTo(sx, sy + ss);
      ctx.lineTo(sx - ss * 0.30, sy + ss * 0.30);
      ctx.lineTo(sx - ss, sy);
      ctx.lineTo(sx - ss * 0.30, sy - ss * 0.30);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }

  /** 調亮/調暗一個 #rrggbb 顏色（amt 為 -255..255 的加法位移）。防呆：非 #hex 回退中性色。 */
  private _shade(hex: string, amt: number): string {
    if (typeof hex !== 'string' || hex.charAt(0) !== '#') hex = '#8a93a8';
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full, 16);
    const cl = (v: number) => Math.max(0, Math.min(255, Math.round(v + amt)));
    return `rgb(${cl((n >> 16) & 255)},${cl((n >> 8) & 255)},${cl(n & 255)})`;
  }

  /** 畫一格 iso 頂面（漸層 + 每型別紋理 + 外框）。`highlight` 非 null 時直接覆蓋純色。 */
  private _isoTopFill(cx: number, topY: number, cell: TerrainCell | 'none', baseTop: string, highlight: string | null): void {
    const TWH = ISO_TW / 2, THH = ISO_TH / 2;
    const diamond = () => {
      ctx.beginPath();
      ctx.moveTo(cx, topY - THH); ctx.lineTo(cx + TWH, topY);
      ctx.lineTo(cx, topY + THH); ctx.lineTo(cx - TWH, topY); ctx.closePath();
    };
    if (highlight) {
      ctx.fillStyle = highlight; diamond(); ctx.fill();
    } else if (cell === 'none') {
      // 一般地板：純色平塗（不加漸層，避免整片棋盤太花）。
      ctx.fillStyle = baseTop; diamond(); ctx.fill();
    } else {
      // 特殊地形：頂面漸層 + 型別紋理。
      const grad = ctx.createLinearGradient(cx, topY - THH, cx, topY + THH);
      grad.addColorStop(0, this._shade(baseTop, 24));
      grad.addColorStop(1, this._shade(baseTop, -20));
      ctx.fillStyle = grad; diamond(); ctx.fill();
      this._terrainDecor(cell, cx, topY);
    }
    ctx.strokeStyle = 'rgba(10,16,28,0.5)'; ctx.lineWidth = 1; diamond(); ctx.stroke();
  }

  /** 地形頂面的小紋理（草葉 / 水波 / 沼氣泡 / 火星 / 石裂），部分帶輕微時間動畫。 */
  private _terrainDecor(cell: TerrainCell | 'none', cx: number, cy: number): void {
    const TWH = ISO_TW / 2, THH = ISO_TH / 2;
    const t = performance.now() / 1000;
    if (cell === 'grass') {
      ctx.strokeStyle = 'rgba(158,224,138,0.55)'; ctx.lineWidth = 1.4;
      for (let i = 0; i < 5; i++) {
        const bx = cx + (i - 2) * TWH * 0.30;
        const by = cy + (((i % 2) - 0.5)) * THH * 0.5 + THH * 0.1;
        const sway = Math.sin(t * 2 + i) * 1.2;
        ctx.beginPath();
        ctx.moveTo(bx, by + 3); ctx.lineTo(bx - 2 + sway, by - 3);
        ctx.moveTo(bx, by + 3); ctx.lineTo(bx + 2 + sway, by - 3);
        ctx.stroke();
      }
    } else if (cell === 'puddle') {
      // 靜態高光 + 兩道緩慢向外擴散的漣漪（錯開相位）。
      ctx.fillStyle = 'rgba(150,200,240,0.16)';
      ctx.beginPath(); ctx.ellipse(cx, cy, TWH * 0.5, THH * 0.5, 0, 0, Math.PI * 2); ctx.fill();
      for (let k = 0; k < 2; k++) {
        const ph = (t * 0.32 + k * 0.5) % 1;      // 慢
        const rr = 0.15 + ph * 0.5;               // 由內向外
        const a = (1 - ph) * 0.4;                 // 外圈漸淡
        ctx.strokeStyle = `rgba(175,218,255,${a.toFixed(3)})`; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.ellipse(cx, cy, TWH * rr, THH * rr, 0, 0, Math.PI * 2); ctx.stroke();
      }
    } else if (cell === 'swamp') {
      // 暗斑 + 緩慢上升、到頂淡出的氣泡。
      ctx.fillStyle = 'rgba(38,48,24,0.5)';
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        ctx.beginPath(); ctx.arc(cx + Math.cos(a) * TWH * 0.3, cy + Math.sin(a) * THH * 0.4, 2, 0, Math.PI * 2); ctx.fill();
      }
      for (let k = 0; k < 3; k++) {
        const ph = (t * 0.4 + k * 0.37) % 1;
        const bx = cx + (k - 1) * TWH * 0.22;
        const by = cy + THH * 0.35 - ph * THH * 0.7;
        const a = Math.sin(ph * Math.PI) * 0.55;  // 中途最明顯
        ctx.fillStyle = `rgba(170,190,110,${a.toFixed(3)})`;
        ctx.beginPath(); ctx.arc(bx, by, 1.6 + ph * 0.8, 0, Math.PI * 2); ctx.fill();
      }
    } else if (cell === 'burningGrass') {
      // 輕微火星（比原本慢、少、動幅小，不搶眼）。
      for (let i = 0; i < 3; i++) {
        const ph = (t * 0.9 + i * 0.4) % 1;
        const fx = cx + (i - 1) * TWH * 0.22 + Math.sin(t * 1.5 + i) * 1.5;
        const fy = cy + THH * 0.15 - ph * THH * 0.45;
        const a = Math.sin(ph * Math.PI) * 0.6;
        ctx.fillStyle = i % 2 ? `rgba(255,185,80,${a.toFixed(3)})` : `rgba(255,120,45,${a.toFixed(3)})`;
        ctx.beginPath(); ctx.arc(fx, fy, 1.8, 0, Math.PI * 2); ctx.fill();
      }
    } else if (cell === 'boulder') {
      ctx.strokeStyle = 'rgba(28,28,32,0.6)'; ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx - TWH * 0.3, cy - THH * 0.1); ctx.lineTo(cx, cy + THH * 0.2); ctx.lineTo(cx + TWH * 0.3, cy - THH * 0.2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(200,200,210,0.35)';
      ctx.beginPath(); ctx.moveTo(cx, cy - THH * 0.4); ctx.lineTo(cx + TWH * 0.2, cy); ctx.lineTo(cx - TWH * 0.2, cy); ctx.closePath(); ctx.fill();
    }
  }

  private _battlefield(g: Game): void {
    // Session 9: battlefield is always full brightness. Earlier sessions
    // dimmed it during MATCH3 so the bottom 三消 panel grabs focus, but
    // user wants symmetrical attention — match-3 panel still desaturates
    // during SCHED/EXEC (handled in _match3), but the battlefield stays
    // at 1.0 alpha across all phases.
    const dim = 1.0;

    // ── iso 立體格戰場（ITB 風格）──────────────────────────
    ctx.globalAlpha = dim;
    const TWH = ISO_TW / 2, THH = ISO_TH / 2;
    const terrColors = (cell: TerrainCell | 'none'): { top: string; l: string; r: string; lift: number } => {
      switch (cell) {
        case 'grass':        return { top: '#4e9044', l: '#356a30', r: '#2c5a28', lift: 0 };
        case 'burningGrass': return { top: '#7a3a26', l: '#4a2014', r: '#3a1810', lift: 0 };
        case 'puddle':       return { top: '#2f6aa0', l: '#1f4a72', r: '#193c5c', lift: 0 };
        case 'swamp':        return { top: '#5a6238', l: '#3e4626', r: '#32381e', lift: 0 };
        case 'boulder':      return { top: '#8a8a92', l: '#5f5f66', r: '#4a4a50', lift: 10 };
        default:             return { top: '#3a4a63', l: '#26344a', r: '#1e2a3c', lift: 0 };
      }
    };
    const drawTile = (cx: number, cy: number, hl: string | null) => {
      const c = cellToScreen(cx, cy);
      const cellT: TerrainCell | 'none' = g.terrain ? (g.terrain[cy]?.[cx] ?? 'none') : 'none';
      const col = terrColors(cellT);
      const topY = c.y - col.lift;
      const depth = ISO_DEPTH + (col.lift > 0 ? col.lift : 0);
      const right = { x: c.x + TWH, y: topY },
            bot = { x: c.x, y: topY + THH }, left = { x: c.x - TWH, y: topY };
      ctx.fillStyle = col.l;
      ctx.beginPath(); ctx.moveTo(left.x, left.y); ctx.lineTo(bot.x, bot.y);
      ctx.lineTo(bot.x, bot.y + depth); ctx.lineTo(left.x, left.y + depth); ctx.closePath(); ctx.fill();
      ctx.fillStyle = col.r;
      ctx.beginPath(); ctx.moveTo(bot.x, bot.y); ctx.lineTo(right.x, right.y);
      ctx.lineTo(right.x, right.y + depth); ctx.lineTo(bot.x, bot.y + depth); ctx.closePath(); ctx.fill();
      // 頂面：漸層 + 每型別紋理（highlight 非 null 時覆蓋純色，如 targeting 範圍）。
      this._isoTopFill(c.x, topY, cellT, col.top, hl);
    };
    let tgtSet: Set<string> | null = null;
    if (g.targeting && g.targetPos) {
      const sp = SPELLS[ELEM[g.selSpellType]];
      const rad = sp ? sp.radius : 1;
      tgtSet = new Set<string>();
      for (let r = 0; r < BATTLE_ROWS; r++)
        for (let c = 0; c < BATTLE_COLS; c++)
          if (md({ x: c, y: r }, g.targetPos) <= rad) tgtSet.add(`${c},${r}`);
    }
    for (let s = 0; s <= (BATTLE_COLS - 1) + (BATTLE_ROWS - 1); s++) {
      for (let cx = 0; cx < BATTLE_COLS; cx++) {
        const cy = s - cx;
        if (cy < 0 || cy >= BATTLE_ROWS) continue;
        drawTile(cx, cy, tgtSet && tgtSet.has(`${cx},${cy}`) ? 'rgba(255,150,40,0.6)' : null);
      }
    }

    // Bases (iso)
    for (const base of g.bases) {
      const teamCol = base.team === g.myTeam ? '68,136,255' : '255,68,85';
      for (const t of base.tiles) {
        const c = cellToScreen(t.x, t.y);
        const top = { x: c.x, y: c.y - THH }, right = { x: c.x + TWH, y: c.y },
              bot = { x: c.x, y: c.y + THH }, left = { x: c.x - TWH, y: c.y };
        ctx.beginPath(); ctx.moveTo(top.x, top.y); ctx.lineTo(right.x, right.y);
        ctx.lineTo(bot.x, bot.y); ctx.lineTo(left.x, left.y); ctx.closePath();
        ctx.fillStyle = base.destroyed ? 'rgba(60,30,30,0.6)' : `rgba(${teamCol},0.32)`;
        ctx.fill();
        ctx.strokeStyle = base.destroyed ? 'rgba(255,60,60,0.4)' : `rgba(${teamCol},0.85)`;
        ctx.lineWidth = 2; ctx.stroke();
        if (!base.destroyed) {
          const s = ISO_TH * 0.32;
          ctx.fillStyle = `rgba(${teamCol},0.9)`;
          ctx.fillRect(c.x - s, c.y - s * 1.1, s * 2, s * 1.3);
          ctx.fillRect(c.x - s * 1.25, c.y - s * 1.7, s * 0.5, s * 0.8);
          ctx.fillRect(c.x + s * 0.75, c.y - s * 1.7, s * 0.5, s * 0.8);
        } else {
          ctx.strokeStyle = 'rgba(255,60,60,0.6)'; ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(c.x - 8, c.y - 6); ctx.lineTo(c.x + 8, c.y + 6);
          ctx.moveTo(c.x + 8, c.y - 6); ctx.lineTo(c.x - 8, c.y + 6); ctx.stroke();
        }
      }
      if (!base.destroyed) {
        const tilesC = base.tiles.map((t) => cellToScreen(t.x, t.y));
        const cxB = tilesC.reduce((a, p) => a + p.x, 0) / tilesC.length;
        const minY = Math.min(...tilesC.map((p) => p.y));
        const barW = 60, barX = cxB - barW / 2, barY = minY - THH - 12;
        const hpRatio = clamp(base.hp / base.maxHp, 0, 1);
        ctx.fillStyle = '#222'; ctx.fillRect(barX, barY, barW, 5);
        ctx.fillStyle = hpRatio > 0.5 ? '#44ee44' : hpRatio > 0.25 ? '#eeaa00' : '#ee3333';
        ctx.fillRect(barX, barY, barW * hpRatio, 5);
        ctx.fillStyle = '#ccc'; ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
        ctx.fillText(`${base.hp}/${base.maxHp}`, cxB, barY - 1);
      }
    }

    // Path preview (iso) — 深底墊線 + 亮虛線，提高在深色/草地上的可見度。
    if (g.state !== GS.EXEC) {
      for (const u of g.units) {
        if (u.dead || u.team !== g.myTeam) continue;
        const pts: { x: number; y: number }[] = [cellToScreen(u.pos.x, u.pos.y)];
        for (let i = u.pathIdx; i < u.path.length; i++) pts.push(cellToScreen(u.path[i].x, u.path[i].y));
        if (pts.length < 2) continue;
        ctx.globalAlpha = 0.6 * dim;
        ctx.strokeStyle = 'rgba(8,12,22,0.6)'; ctx.lineWidth = 4; ctx.setLineDash([]);
        ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.stroke();
        ctx.globalAlpha = 0.8 * dim;
        ctx.strokeStyle = '#8ec5ff'; ctx.lineWidth = 2.5; ctx.setLineDash([5, 4]);
        ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.stroke(); ctx.setLineDash([]);
      }
      ctx.globalAlpha = dim;
    }

    // Units (iso, back-to-front by vx+vy so nearer units overlap farther)
    const stackMap = new Map<string, BattleUnit[]>();
    for (const u of g.units) {
      if (u.dead) continue;
      const gx = Math.round(u.vx), gy = Math.round(u.vy);
      const key = `${u.team},${gx},${gy}`;
      if (!stackMap.has(key)) stackMap.set(key, []);
      stackMap.get(key)!.push(u);
    }
    const UNIT_LIFT = ISO_DEPTH + 4;
    const drawOrder = g.units.filter((u) => !u.dead).slice().sort((a, b) => (a.vx + a.vy) - (b.vx + b.vy));
    for (const u of drawOrder) {
      const cfg = UCFG[u.type];
      const gx = Math.round(u.vx), gy = Math.round(u.vy);
      const key = `${u.team},${gx},${gy}`;
      const stackGroup = stackMap.get(key) || [u];
      let stackOff = 0;
      if (stackGroup.length > 1) {
        const idx = stackGroup.indexOf(u);
        stackOff = (idx - (stackGroup.length - 1) / 2) * (ISO_TW * 0.22);
      }
      const c = cellToScreen(u.vx, u.vy);
      const px = c.x + stackOff;
      const py = c.y - UNIT_LIFT;
      const r = stackGroup.length > 1 ? ISO_TW * 0.16 : ISO_TW * 0.22;

      if (u.flash > 0) {
        ctx.globalAlpha = u.flash * dim;
        ctx.fillStyle = u.flashColor;
        ctx.fillRect(px - r - 2, py - r - 2, r * 2 + 4, r * 2 + 4);
        u.flash -= 0.02;
        ctx.globalAlpha = dim;
      }

      const tcol = u.team === g.myTeam ? '#4488ff' : '#ff4455';
      ctx.fillStyle = tcol;
      this._drawUnitShape(cfg.shape, px, py, r);

      const hpRatio = u.totalSoldiers > 0 ? u.totalSoldiers / cfg.hp : 0;
      const bw = (stackGroup.length > 1 ? 28 : 40), bh = 3, bx = px - bw / 2, by = py - r - 7;
      ctx.fillStyle = '#222'; ctx.fillRect(bx, by, bw, bh);
      ctx.fillStyle = hpRatio > 0.5 ? '#44ee44' : hpRatio > 0.25 ? '#eeaa00' : '#ee3333';
      ctx.fillRect(bx, by, bw * clamp(hpRatio, 0, 1), bh);

      ctx.fillStyle = '#fff'; ctx.font = `bold ${Math.max(9, r * 0.8)}px sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(String(u.hp), px, py);
      ctx.fillStyle = 'rgba(255,255,255,0.55)'; ctx.font = '9px sans-serif';
      ctx.fillText(u.type[0], px, py + r + 7);

      this._drawUnitModifierBadges(u, px, by);
    }
    ctx.globalAlpha = 1;
  }

  // ============================================================
  // Card Tactician (M4b) — 卡牌橫向 UI
  // ============================================================

  /** 頂列右側三鈕：返回 ｜ 跳過 ｜ 確認出牌。 */
  static cardConfirmRect(): { x: number; y: number; w: number; h: number } {
    return { x: W - 136, y: 10, w: 120, h: TOP_BAR_H - 20 };
  }
  static cardPassRect(): { x: number; y: number; w: number; h: number } {
    return { x: W - 216, y: 10, w: 70, h: TOP_BAR_H - 20 };
  }
  static cardBackRect(): { x: number; y: number; w: number; h: number } {
    return { x: W - 296, y: 10, w: 70, h: TOP_BAR_H - 20 };
  }
  /** v2 — 跳 tick 抽牌鈕（單機；棄掉目前 tick 改從棄牌堆隨機抽 1 張）。 */
  static cardDrawRect(): { x: number; y: number; w: number; h: number } {
    return { x: W - 386, y: 10, w: 84, h: TOP_BAR_H - 20 };
  }

  /** 第 i 個 Tick 槽命中矩形（時間軸）。 */
  static cardTickRect(i: number): { x: number; y: number; w: number; h: number } {
    const m = 40, gap = 12;
    const y = H - HAND_H - TIMELINE_H + 4, h = TIMELINE_H - 8;
    const tw = (W - 2 * m - 2 * gap) / 3;
    return { x: m + i * (tw + gap), y, w: tw, h };
  }

  /** 第 i 張手牌命中矩形（依手牌數置中）。
   *  ch 收窄 + 下方留白，避免卡片太貼畫面底緣（見 _cardFx baseH 需同步）。 */
  static cardHandRect(i: number, count: number): { x: number; y: number; w: number; h: number } {
    const cw = 92, ch = HAND_H - 46, gap = 10;
    const total = count * cw + (count - 1) * gap;
    const sx = (W - total) / 2;
    return { x: sx + i * (cw + gap), y: H - HAND_H + 14, w: cw, h: ch };
  }

  /** 投降按鈕命中矩形（左欄下方）。 */
  static cardSurrenderRect(): { x: number; y: number; w: number; h: number } {
    return { x: 12, y: TOP_BAR_H + 152, w: LEFT_RAIL_W - 24, h: 30 };
  }

  /** 牌庫牌堆矩形（左欄；抽牌動畫飛出起點）。 */
  static deckPileRect(): { x: number; y: number; w: number; h: number } {
    return { x: 16, y: TOP_BAR_H + 20, w: 56, h: 76 };
  }
  /** 棄牌牌堆矩形（左欄；出牌/棄牌動畫飛入終點）。 */
  static discardPileRect(): { x: number; y: number; w: number; h: number } {
    return { x: 84, y: TOP_BAR_H + 20, w: 56, h: 76 };
  }

  /** 搜牌選單第 i 個牌庫選項命中矩形（置中橫列）。 */
  static tutorOptionRect(i: number, count: number): { x: number; y: number; w: number; h: number } {
    const cw = 86, ch = 104, gap = 12;
    const total = count * cw + (count - 1) * gap;
    const sx = (W - total) / 2;
    return { x: sx + i * (cw + gap), y: Math.round(H * 0.34), w: cw, h: ch };
  }

  /** 圓角矩形 path（卡牌繪製共用；呼叫端自行 fill / stroke / clip）。 */
  private _rrPath(x: number, y: number, w: number, h: number, rad: number): void {
    ctx.beginPath();
    ctx.moveTo(x + rad, y);
    ctx.arcTo(x + w, y, x + w, y + h, rad);
    ctx.arcTo(x + w, y + h, x, y + h, rad);
    ctx.arcTo(x, y + h, x, y, rad);
    ctx.arcTo(x, y, x + w, y, rad);
    ctx.closePath();
  }

  /** 依卡牌決定圖騰 key（元素名 或 濾牌 filterKind）。 */
  private _glyphKey(def: { element?: number; filterKind?: string }): string {
    return def.element !== undefined ? ELEM[def.element] : (def.filterKind ?? 'draw');
  }

  /** #rrggbb → rgba(...,a)（卡牌元素色微染用）。防呆：非 #hex 一律回退中性色，避免 crash。 */
  private _rgba(hex: string, a: number): string {
    if (typeof hex !== 'string' || hex.charAt(0) !== '#') hex = '#8a93a8';
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full, 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  }

  /** 程序化法術圖騰（繪於 (cx,cy)、尺寸 s；用當前 ctx.fill/strokeStyle）。 */
  private _magicGlyph(key: string, cx: number, cy: number, s: number): void {
    ctx.lineWidth = Math.max(1.4, s * 0.17);
    switch (key) {
      case 'Fire': // 火焰
        ctx.beginPath();
        ctx.moveTo(cx, cy - s);
        ctx.quadraticCurveTo(cx + s * 0.95, cy - s * 0.1, cx + s * 0.5, cy + s * 0.55);
        ctx.quadraticCurveTo(cx + s * 0.25, cy + s, cx, cy + s * 0.68);
        ctx.quadraticCurveTo(cx - s * 0.25, cy + s, cx - s * 0.5, cy + s * 0.55);
        ctx.quadraticCurveTo(cx - s * 0.95, cy - s * 0.1, cx, cy - s);
        ctx.closePath(); ctx.fill();
        break;
      case 'Lightning': // 閃電
        ctx.beginPath();
        ctx.moveTo(cx + s * 0.35, cy - s); ctx.lineTo(cx - s * 0.45, cy + s * 0.05);
        ctx.lineTo(cx + s * 0.02, cy + s * 0.05); ctx.lineTo(cx - s * 0.3, cy + s);
        ctx.lineTo(cx + s * 0.55, cy - s * 0.25); ctx.lineTo(cx + s * 0.05, cy - s * 0.25);
        ctx.closePath(); ctx.fill();
        break;
      case 'Water': // 水滴（補血）
        ctx.beginPath();
        ctx.moveTo(cx, cy - s);
        ctx.bezierCurveTo(cx + s * 0.9, cy - s * 0.05, cx + s * 0.62, cy + s, cx, cy + s);
        ctx.bezierCurveTo(cx - s * 0.62, cy + s, cx - s * 0.9, cy - s * 0.05, cx, cy - s);
        ctx.closePath(); ctx.fill();
        break;
      case 'Wind': // 盾牌（護盾）
        ctx.beginPath();
        ctx.moveTo(cx, cy - s); ctx.lineTo(cx + s * 0.82, cy - s * 0.55); ctx.lineTo(cx + s * 0.82, cy + s * 0.2);
        ctx.quadraticCurveTo(cx + s * 0.6, cy + s * 0.95, cx, cy + s);
        ctx.quadraticCurveTo(cx - s * 0.6, cy + s * 0.95, cx - s * 0.82, cy + s * 0.2);
        ctx.lineTo(cx - s * 0.82, cy - s * 0.55); ctx.closePath(); ctx.fill();
        break;
      case 'Light': // 劍（加攻）
        ctx.fillRect(cx - s * 0.12, cy - s, s * 0.24, s * 1.4);
        ctx.fillRect(cx - s * 0.5, cy + s * 0.35, s * 1.0, s * 0.18);
        ctx.fillRect(cx - s * 0.09, cy + s * 0.55, s * 0.18, s * 0.35);
        break;
      case 'WindFriendly': case 'WindHostile': { // 旋渦
        ctx.beginPath(); let rad = s * 0.12, a = 0; ctx.moveTo(cx + rad, cy);
        for (let i = 0; i < 44; i++) { a += 0.42; rad = s * 0.12 + i * (s * 0.86 / 44); ctx.lineTo(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad); }
        ctx.stroke(); break;
      }
      case 'Grow': { // 幼苗（長草）
        ctx.beginPath(); ctx.moveTo(cx, cy + s); ctx.lineTo(cx, cy - s * 0.35); ctx.stroke();
        const leaf = (d: number) => { ctx.beginPath(); ctx.moveTo(cx, cy - s * 0.05);
          ctx.quadraticCurveTo(cx + d * s * 0.85, cy - s * 0.2, cx + d * s * 0.22, cy - s * 0.95);
          ctx.quadraticCurveTo(cx + d * s * 0.08, cy - s * 0.4, cx, cy - s * 0.05); ctx.closePath(); ctx.fill(); };
        leaf(1); leaf(-1); break;
      }
      case 'Rain': // 雨雲（降雨）
        ctx.beginPath();
        ctx.arc(cx - s * 0.4, cy - s * 0.15, s * 0.4, 0, Math.PI * 2);
        ctx.arc(cx + s * 0.05, cy - s * 0.4, s * 0.5, 0, Math.PI * 2);
        ctx.arc(cx + s * 0.5, cy - s * 0.15, s * 0.36, 0, Math.PI * 2);
        ctx.fill();
        for (let i = -1; i <= 1; i++) { ctx.beginPath(); ctx.moveTo(cx + i * s * 0.4, cy + s * 0.3); ctx.lineTo(cx + i * s * 0.4 - s * 0.12, cy + s); ctx.stroke(); }
        break;
      case 'Rockfall': // 落石
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.75, cy + s * 0.45); ctx.lineTo(cx - s * 0.3, cy - s * 0.5); ctx.lineTo(cx + s * 0.4, cy - s * 0.6);
        ctx.lineTo(cx + s * 0.82, cy + s * 0.2); ctx.lineTo(cx + s * 0.4, cy + s * 0.75); ctx.lineTo(cx - s * 0.4, cy + s * 0.7);
        ctx.closePath(); ctx.fill();
        break;
      case 'tutor': // 搜牌：放大鏡
        ctx.beginPath(); ctx.arc(cx - s * 0.18, cy - s * 0.18, s * 0.5, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx + s * 0.2, cy + s * 0.2); ctx.lineTo(cx + s * 0.75, cy + s * 0.75); ctx.stroke();
        break;
      case 'cycle': // 調度：循環箭頭
        ctx.beginPath(); ctx.arc(cx, cy, s * 0.6, -Math.PI * 0.5, Math.PI * 1.1); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx - s * 0.25, cy - s * 0.55); ctx.lineTo(cx + s * 0.02, cy - s * 0.6); ctx.lineTo(cx + s * 0.02, cy - s * 0.28); ctx.closePath(); ctx.fill();
        break;
      case 'draw': default: // 抽牌：卡片 + 下箭頭
        ctx.strokeRect(cx - s * 0.55, cy - s * 0.8, s * 1.1, s * 1.2);
        ctx.beginPath(); ctx.moveTo(cx, cy - s * 0.25); ctx.lineTo(cx, cy + s * 0.45);
        ctx.moveTo(cx - s * 0.3, cy + s * 0.1); ctx.lineTo(cx, cy + s * 0.45); ctx.lineTo(cx + s * 0.3, cy + s * 0.1); ctx.stroke();
        break;
    }
  }

  /** 卡牌魔法徽章：暗底圓盤 + 發光符文環 + 元素法術圖騰。 */
  private _cardMedallion(def: { element?: number; filterKind?: string }, cx: number, cy: number, r: number): void {
    const accent = (def.element !== undefined ? ECOL[ELEM[def.element]] : undefined) ?? '#8a93a8';
    const key = this._glyphKey(def);
    // 元素色圓盤（中央暗色元素光 → 邊緣深底）+ 外發光
    ctx.save();
    ctx.shadowColor = accent; ctx.shadowBlur = 10;
    const disc = ctx.createRadialGradient(cx, cy - r * 0.2, r * 0.1, cx, cy, r);
    disc.addColorStop(0, this._shade(accent, -70));
    disc.addColorStop(0.65, this._shade(accent, -120));
    disc.addColorStop(1, '#0a0f1c');
    ctx.fillStyle = disc; ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // 符文外環 + 內環 + 刻痕
    ctx.strokeStyle = accent; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    ctx.save(); ctx.globalAlpha = 0.45; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.8, 0, Math.PI * 2); ctx.stroke();
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * r * 0.84, cy + Math.sin(a) * r * 0.84);
      ctx.lineTo(cx + Math.cos(a) * r * 0.97, cy + Math.sin(a) * r * 0.97);
      ctx.stroke();
    }
    ctx.restore();
    // 法術圖騰（提亮的元素色 + 發光，在暗色元素圓盤上更跳）
    ctx.save();
    ctx.shadowColor = accent; ctx.shadowBlur = 6;
    const glow = this._shade(accent, 55);
    ctx.strokeStyle = glow; ctx.fillStyle = glow; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    this._magicGlyph(key, cx, cy, r * 0.5);
    ctx.restore();
  }

  /** 卡背（牌堆 / 抽牌翻面前）。top-left + 尺寸。 */
  private _drawCardBack(x: number, y: number, w: number, h: number): void {
    const rad = Math.max(3, w * 0.1);
    this._rrPath(x, y, w, h, rad); ctx.fillStyle = '#2a3350'; ctx.fill();
    ctx.save(); this._rrPath(x, y, w, h, rad); ctx.clip();
    ctx.strokeStyle = 'rgba(120,150,210,0.30)'; ctx.lineWidth = 2;
    for (let i = -h; i < w; i += 10) {
      ctx.beginPath(); ctx.moveTo(x + i, y); ctx.lineTo(x + i + h, y + h); ctx.stroke();
    }
    const cx = x + w / 2, cy = y + h / 2, rw = w * 0.22, rh = h * 0.22;
    ctx.fillStyle = 'rgba(150,180,240,0.5)';
    ctx.beginPath();
    ctx.moveTo(cx, cy - rh); ctx.lineTo(cx + rw, cy); ctx.lineTo(cx, cy + rh); ctx.lineTo(cx - rw, cy);
    ctx.closePath(); ctx.fill();
    ctx.restore();
    this._rrPath(x, y, w, h, rad); ctx.strokeStyle = 'rgba(130,160,210,0.7)'; ctx.lineWidth = 1.5; ctx.stroke();
  }

  /** 卡面（簡版：元素色頂帶 + 徽記 + 名稱）。飛行卡 / 牌堆頂張共用。 */
  private _drawCardFaceMini(x: number, y: number, w: number, h: number, cardId: string): void {
    const def = CARDS[cardId];
    if (!def) return;
    const accent = (def.element !== undefined ? ECOL[ELEM[def.element]] : undefined) ?? '#8a93a8';
    const rad = Math.max(3, w * 0.1);
    ctx.fillStyle = '#171d2c'; this._rrPath(x, y, w, h, rad); ctx.fill();
    ctx.save(); this._rrPath(x, y, w, h, rad); ctx.clip();
    const washm = ctx.createLinearGradient(x, y, x, y + h);
    washm.addColorStop(0, this._rgba(accent, 0.30)); washm.addColorStop(1, this._rgba(accent, 0.04));
    ctx.fillStyle = washm; ctx.fillRect(x, y, w, h);
    const hbm = ctx.createLinearGradient(x, y, x, y + h * 0.2);
    hbm.addColorStop(0, this._shade(accent, 45)); hbm.addColorStop(1, accent);
    ctx.fillStyle = hbm; ctx.fillRect(x, y, w, h * 0.2);
    ctx.restore();
    this._cardMedallion(def, x + w / 2, y + h * 0.5, w * 0.22);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = `bold ${Math.round(h * 0.1)}px sans-serif`;
    ctx.lineWidth = 2.5; ctx.strokeStyle = 'rgba(8,12,22,0.85)';
    ctx.strokeText(def.name, x + w / 2, y + h * 0.11);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(def.name, x + w / 2, y + h * 0.11);
    this._rrPath(x, y, w, h, rad); ctx.strokeStyle = this._rgba(accent, 0.55); ctx.lineWidth = 1.2; ctx.stroke();
    ctx.textBaseline = 'alphabetic';
  }

  /** 左欄一疊牌堆（卡背/翻面頂張 + 疊層 + 數量徽記 + 標籤）。 */
  private _drawPile(
    r: { x: number; y: number; w: number; h: number },
    label: string, count: number, faceUp: boolean, topCardId?: string,
  ): void {
    const rad = Math.max(3, r.w * 0.1);
    if (count <= 0) {
      this._rrPath(r.x, r.y, r.w, r.h, rad);
      ctx.setLineDash([4, 4]); ctx.strokeStyle = 'rgba(90,110,150,0.4)'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.setLineDash([]);
    } else {
      const layers = Math.min(3, count);
      for (let i = layers - 1; i >= 1; i--) {
        const ox = i * 2.5;
        this._rrPath(r.x + ox, r.y - ox, r.w, r.h, rad);
        ctx.fillStyle = '#1c2438'; ctx.fill();
        ctx.strokeStyle = 'rgba(90,110,150,0.5)'; ctx.lineWidth = 1; ctx.stroke();
      }
      if (faceUp && topCardId) this._drawCardFaceMini(r.x, r.y, r.w, r.h, topCardId);
      else this._drawCardBack(r.x, r.y, r.w, r.h);
    }
    ctx.fillStyle = '#9fb2d0'; ctx.font = '11px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    ctx.fillText(label, r.x + r.w / 2, r.y + r.h + 4);
    // 數量徽記（右下角）
    const bx = r.x + r.w - 2, by = r.y + r.h - 2;
    ctx.fillStyle = 'rgba(10,14,24,0.9)'; ctx.beginPath(); ctx.arc(bx, by, 11, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(140,170,220,0.8)'; ctx.lineWidth = 1; ctx.stroke();
    ctx.fillStyle = '#e6edf7'; ctx.font = 'bold 12px sans-serif'; ctx.textBaseline = 'middle';
    ctx.fillText(String(count), bx, by + 0.5);
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
  }

  /** Card Tactician — 飛行卡片動畫層（抽牌從牌庫飛入手牌 / 出牌飛進 tick）。 */
  private _cardFx(g: Game): void {
    if (!g.cardFx || g.cardFx.length === 0) return;
    const baseW = 92, baseH = HAND_H - 46; // 與 cardHandRect 的 cw/ch 同步
    for (const c of g.cardFx) {
      const w = baseW * c.scale, h = baseH * c.scale;
      const flipSquash = Math.abs(1 - 2 * c.flip); // 1→0→1：翻面時中段壓扁
      ctx.save();
      ctx.globalAlpha = clamp(c.alpha, 0, 1);
      ctx.translate(c.x, c.y);
      ctx.scale(Math.max(0.02, flipSquash), 1);
      if (c.flip >= 0.5) this._drawCardFaceMini(-w / 2, -h / 2, w, h, c.cardId);
      else this._drawCardBack(-w / 2, -h / 2, w, h);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /** 出牌選位引導：targeting 時在戰場中央顯示提示 chip + 游標格十字準星，
   *  引導玩家「點戰場格放置」而非把卡拖到下方 tick 槽。 */
  private _targetingHint(g: Game): void {
    const t = performance.now() / 1000;
    const pulse = 0.5 + 0.5 * Math.sin(t * 3);
    // 游標所在格的十字準星（跟著滑鼠移動的 g.targetPos）——比整片板框高亮更聚焦。
    if (g.targetPos) {
      const p = cellToScreen(g.targetPos.x, g.targetPos.y);
      ctx.save();
      ctx.strokeStyle = `rgba(140,210,255,${(0.55 + 0.35 * pulse).toFixed(3)})`;
      ctx.lineWidth = 2;
      const rr = ISO_TW * 0.42;
      // 菱形準星（貼合 iso 格）
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - ISO_TH * 0.42); ctx.lineTo(p.x + rr, p.y);
      ctx.lineTo(p.x, p.y + ISO_TH * 0.42); ctx.lineTo(p.x - rr, p.y); ctx.closePath();
      ctx.stroke();
      // 中心十字
      ctx.beginPath();
      ctx.moveTo(p.x - 7, p.y); ctx.lineTo(p.x + 7, p.y);
      ctx.moveTo(p.x, p.y - 5); ctx.lineTo(p.x, p.y + 5); ctx.stroke();
      ctx.restore();
    }
    // 中央提示 chip
    const sp = SPELLS[ELEM[g.selSpellType]];
    const n = g.cardQueueUids.length;
    const label = `◈ 點戰場格放置${sp ? `：${sp.name}` : ''}${n > 1 ? ` ×${n}` : ''}`;
    const cx = FIELD_X + FIELD_W / 2, y = FIELD_Y + 24;
    ctx.font = 'bold 16px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const w = ctx.measureText(label).width + 30;
    ctx.globalAlpha = 0.9 + 0.1 * pulse;
    ctx.fillStyle = 'rgba(18,36,64,0.92)'; this._rrPath(cx - w / 2, y - 16, w, 32, 9); ctx.fill();
    ctx.strokeStyle = `rgba(130,205,255,${(0.6 + 0.4 * pulse).toFixed(3)})`; ctx.lineWidth = 2;
    this._rrPath(cx - w / 2, y - 16, w, 32, 9); ctx.stroke();
    ctx.fillStyle = '#e8f4ff'; ctx.fillText(label, cx, y);
    ctx.globalAlpha = 1; ctx.textBaseline = 'alphabetic';
  }

  private _cardHud(g: Game): void {
    ctx.fillStyle = 'rgba(20,26,42,0.92)'; ctx.fillRect(0, 0, W, TOP_BAR_H);
    ctx.fillStyle = 'rgba(80,110,160,0.5)'; ctx.fillRect(0, TOP_BAR_H - 1, W, 1);
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#cdd6e6'; ctx.font = '15px sans-serif'; ctx.textAlign = 'left';
    ctx.fillText(`Round ${g.round} / ${g.maxRounds}`, 16, TOP_BAR_H / 2);
    ctx.fillStyle = '#8aa0c0'; ctx.font = '13px sans-serif';
    ctx.fillText(g.state === GS.EXEC ? '演繹中…' : '規劃出牌', 180, TOP_BAR_H / 2);
    // 基地 HP 摘要（置中）
    const myBase = g.bases.find((b) => b.team === g.myTeam);
    const foeBase = g.bases.find((b) => b.team !== g.myTeam);
    ctx.textAlign = 'center'; ctx.font = '13px sans-serif';
    ctx.fillStyle = '#6cacff';
    ctx.fillText(`我方 ${myBase ? myBase.hp : 0}`, W / 2 - 70, TOP_BAR_H / 2);
    ctx.fillStyle = '#ff6a78';
    ctx.fillText(`敵方 ${foeBase ? foeBase.hp : 0}`, W / 2 + 70, TOP_BAR_H / 2);
    // 規劃控制鈕（僅 SCHED）：返回 ｜ 跳過 ｜ 確認出牌
    if (g.state === GS.SCHED) {
      const btn = (r: { x: number; y: number; w: number; h: number }, label: string, fill: string, stroke: string, fg: string) => {
        ctx.fillStyle = fill; ctx.fillRect(r.x, r.y, r.w, r.h);
        ctx.strokeStyle = stroke; ctx.lineWidth = 1.5; ctx.strokeRect(r.x, r.y, r.w, r.h);
        ctx.fillStyle = fg; ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2);
      };
      // v2 跳 tick 抽牌鈕（僅單機；可用時亮綠、不可用時灰）。
      if (g.gameMode === 'single') {
        const dk = g.playerDeck;
        const drawOk = !!dk && dk.counts.discard > 0
          && g.spellSlot >= 0 && g.spellSlot <= 2 && !g.tickDrawUsed[g.spellSlot];
        if (drawOk) btn(Renderer.cardDrawRect(), '棄→抽1', 'rgba(30,64,44,0.7)', '#6fdca0', '#dfffe8');
        else btn(Renderer.cardDrawRect(), '棄→抽1', 'rgba(40,44,56,0.5)', 'rgba(120,140,160,0.4)', 'rgba(150,170,190,0.5)');
      }
      btn(Renderer.cardBackRect(), '◀ 返回', 'rgba(50,54,70,0.7)', '#8aa0c0', '#cdd6e6');
      btn(Renderer.cardPassRect(), '跳過', 'rgba(50,54,70,0.7)', '#8aa0c0', '#cdd6e6');
      btn(Renderer.cardConfirmRect(), '確認出牌 ▶', 'rgba(40,90,150,0.6)', '#6cacff', '#dff0ff');
    }
    ctx.textBaseline = 'alphabetic';
  }

  private _cardTimeline(g: Game): void {
    ctx.textBaseline = 'middle';
    const targeting = g.state === GS.SCHED && g.targeting;
    for (let i = 0; i < 3; i++) {
      const r = Renderer.cardTickRect(i);
      // v2：已用來抽牌的 tick → 暗掉，顯示「棄牌抽 1」。
      if (g.tickDrawUsed[i]) {
        ctx.fillStyle = 'rgba(18,24,36,0.9)'; ctx.fillRect(r.x, r.y, r.w, r.h);
        ctx.strokeStyle = 'rgba(90,150,110,0.5)'; ctx.lineWidth = 1; ctx.strokeRect(r.x, r.y, r.w, r.h);
        ctx.fillStyle = '#5f7466'; ctx.font = '12px sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(`Tick ${i + 1}`, r.x + 10, r.y + r.h / 2);
        ctx.fillStyle = '#8fd6a2'; ctx.textAlign = 'center';
        ctx.fillText('棄牌抽 1', r.x + r.w / 2 + 24, r.y + r.h / 2);
        continue;
      }
      const active = g.spellSlot === i && g.state === GS.SCHED;
      ctx.fillStyle = active ? 'rgba(60,110,170,0.4)' : 'rgba(28,36,54,0.85)';
      ctx.fillRect(r.x, r.y, r.w, r.h);
      ctx.strokeStyle = active ? '#6cacff' : 'rgba(90,110,150,0.5)';
      ctx.lineWidth = active ? 2 : 1; ctx.strokeRect(r.x, r.y, r.w, r.h);
      // 出牌放置閃光（confirmTarget 觸發，_updateCardFx 衰減）。
      const flash = g.tickFlash[i] || 0;
      if (flash > 0) {
        ctx.save();
        ctx.globalAlpha = clamp(flash, 0, 1);
        ctx.fillStyle = 'rgba(120,200,255,0.45)'; ctx.fillRect(r.x, r.y, r.w, r.h);
        ctx.strokeStyle = '#bfe3ff'; ctx.lineWidth = 2.5; ctx.strokeRect(r.x, r.y, r.w, r.h);
        ctx.restore();
      }
      ctx.fillStyle = '#9fb2d0'; ctx.font = '12px sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`Tick ${i + 1}`, r.x + 10, r.y + r.h / 2);
      const cmd = g.pSpells[i];
      ctx.textAlign = 'center';
      if (cmd && cmd.spellType !== 0) {
        const sp = SPELLS[ELEM[cmd.spellType]];
        const stack = cmd.stackCount && cmd.stackCount > 1 ? ` ×${cmd.stackCount}` : '';
        ctx.fillStyle = '#e6edf7'; ctx.font = 'bold 14px sans-serif';
        ctx.fillText((sp ? sp.name : '?') + stack, r.x + r.w / 2 + 24, r.y + r.h / 2);
      } else if (targeting && active) {
        // 引導：出牌是「點戰場格」，不是拖到這個 tick 槽。
        ctx.fillStyle = '#7fc8ff'; ctx.font = 'bold 12px sans-serif';
        ctx.fillText('點戰場格放置 ↑', r.x + r.w / 2 + 24, r.y + r.h / 2);
      } else {
        ctx.fillStyle = '#5f6e86'; ctx.font = '13px sans-serif';
        ctx.fillText('— 空 —', r.x + r.w / 2 + 24, r.y + r.h / 2);
      }
    }
    ctx.textBaseline = 'alphabetic';
  }

  private _cardHand(g: Game): void {
    ctx.fillStyle = 'rgba(18,22,36,0.95)'; ctx.fillRect(0, H - HAND_H, W, HAND_H);
    ctx.fillStyle = 'rgba(80,110,160,0.4)'; ctx.fillRect(0, H - HAND_H, W, 1);
    const deck = g.playerDeck;
    if (!deck) return;
    const hand = deck.hand;
    if (hand.length === 0) {
      ctx.fillStyle = '#5f6e86'; ctx.font = '14px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('手牌已空', W / 2, H - HAND_H / 2);
      ctx.textBaseline = 'alphabetic';
      return;
    }
    // 圓角矩形 path helper（卡牌感）。
    const rr = (x: number, y: number, w: number, h: number, rad: number) => {
      ctx.beginPath();
      ctx.moveTo(x + rad, y);
      ctx.arcTo(x + w, y, x + w, y + h, rad);
      ctx.arcTo(x + w, y + h, x, y + h, rad);
      ctx.arcTo(x, y + h, x, y, rad);
      ctx.arcTo(x, y, x + w, y, rad);
      ctx.closePath();
    };
    for (let i = 0; i < hand.length; i++) {
      const r = Renderer.cardHandRect(i, hand.length);
      const card = hand[i];
      // 飛行中（抽牌動畫尚未落定）的牌先不畫靜態卡，避免重影。
      if (g.isCardAnimating(card.uid)) continue;
      const def = CARDS[card.cardId];
      if (!def) continue;
      const accent = (def.element !== undefined ? ECOL[ELEM[def.element]] : undefined) ?? '#8a93a8';
      const queued = g.isCardQueued(card.uid);
      const canStack = g.canStackOnQueue(card.uid);
      const cycMarked = g.cycleMode && g.cycleMarks.includes(card.uid);
      const cycCard = g.cycleMode && g.cycleCardUid === card.uid;
      const raised = queued || canStack || cycMarked || cycCard;
      const y = raised ? r.y - 12 : r.y;
      const rad = 9;

      // 卡身：深底（圓角）
      ctx.fillStyle = '#171d2c';
      rr(r.x, y, r.w, r.h, rad); ctx.fill();
      ctx.save();
      rr(r.x, y, r.w, r.h, rad); ctx.clip();
      // 元素色微染（上濃下淡）→ 每張卡有自己的色彩身分
      const wash = ctx.createLinearGradient(r.x, y, r.x, y + r.h);
      wash.addColorStop(0, this._rgba(accent, 0.30));
      wash.addColorStop(0.5, this._rgba(accent, 0.11));
      wash.addColorStop(1, this._rgba(accent, 0.03));
      ctx.fillStyle = wash; ctx.fillRect(r.x, y, r.w, r.h);
      // 頂帶（元素色漸層）
      const hb = ctx.createLinearGradient(r.x, y, r.x, y + 26);
      hb.addColorStop(0, this._shade(accent, 45)); hb.addColorStop(1, accent);
      ctx.fillStyle = hb; ctx.fillRect(r.x, y, r.w, 26);
      // 淡淡的法陣浮水印（元素圖騰放大、低透明度）
      ctx.globalAlpha = 0.13;
      ctx.strokeStyle = accent; ctx.fillStyle = accent; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      this._magicGlyph(this._glyphKey(def), r.x + r.w / 2, y + r.h * 0.66, r.w * 0.36);
      ctx.restore();
      // 魔法徽章（暗底圓盤 + 發光符文環 + 元素法術圖騰，取代原本的單字徽記）
      const emY = y + 58;
      this._cardMedallion(def, r.x + r.w / 2, emY, 20);
      // 名稱（白字 + 深色描邊：在任何元素色頂帶上都清楚）
      ctx.font = 'bold 14px sans-serif'; ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
      ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(8,12,22,0.85)';
      ctx.strokeText(def.name, r.x + r.w / 2, y + 13);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(def.name, r.x + r.w / 2, y + 13);
      // 分類 / 狀態標
      const tag = def.category === 'stackable' ? '疊加' : def.category === 'effect' ? '效果' : '濾牌';
      let footer = tag, footerCol = '#c8d2e4';
      if (cycMarked) { footer = '棄'; footerCol = '#ff8a8a'; }
      else if (cycCard) { footer = '再點＝確認'; footerCol = '#7fe0ff'; }
      else if (canStack) { footer = '＋疊加'; footerCol = '#9be8a6'; }
      else if (queued) { footer = '已選'; footerCol = '#ffd166'; }
      ctx.fillStyle = footerCol; ctx.font = '11px sans-serif'; ctx.textBaseline = 'alphabetic';
      ctx.fillText(footer, r.x + r.w / 2, y + r.h - 9);

      // 邊框（依狀態；預設用元素色，讓卡有色彩身分）
      let bcol = this._rgba(accent, 0.6), bw = 1.5;
      if (cycMarked) { bcol = '#ff6a6a'; bw = 2.5; }
      else if (cycCard) { bcol = '#5ec8ff'; bw = 2.5; }
      else if (queued) { bcol = '#ffd166'; bw = 2.5; }
      else if (canStack) {
        const pulse = 0.55 + 0.45 * Math.sin(performance.now() / 220);
        bcol = `rgba(120,230,140,${pulse.toFixed(2)})`; bw = 2.5;
      }
      ctx.strokeStyle = bcol; ctx.lineWidth = bw;
      rr(r.x, y, r.w, r.h, rad); ctx.stroke();
    }
    ctx.textBaseline = 'alphabetic';
  }

  /** 搜牌（tutor）牌庫選單覆蓋層：列出牌庫各牌種，點一張上手。 */
  private _cardTutorOverlay(g: Game): void {
    // 暗化戰場區（不蓋頂列 / 手牌列，方便看到返回鈕與手牌）。
    ctx.fillStyle = 'rgba(8,10,18,0.74)';
    ctx.fillRect(0, TOP_BAR_H, W, H - TOP_BAR_H - HAND_H);
    ctx.fillStyle = '#e6edf7'; ctx.font = 'bold 18px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    ctx.fillText('搜牌：從牌庫選一張上手（右上「返回」取消）', W / 2, Math.round(H * 0.34) - 22);
    const opts = g.tutorOptions();
    if (opts.length === 0) {
      ctx.fillStyle = '#9fb2d0'; ctx.font = '14px sans-serif';
      ctx.fillText('牌庫目前沒有可搜的牌', W / 2, Math.round(H * 0.34) + 50);
      return;
    }
    const rr = (x: number, y: number, w: number, h: number, rad: number) => {
      ctx.beginPath(); ctx.moveTo(x + rad, y);
      ctx.arcTo(x + w, y, x + w, y + h, rad); ctx.arcTo(x + w, y + h, x, y + h, rad);
      ctx.arcTo(x, y + h, x, y, rad); ctx.arcTo(x, y, x + w, y, rad); ctx.closePath();
    };
    for (let i = 0; i < opts.length; i++) {
      const r = Renderer.tutorOptionRect(i, opts.length);
      const def = CARDS[opts[i].cardId];
      if (!def) continue;
      const accent = (def.element !== undefined ? ECOL[ELEM[def.element]] : undefined) ?? '#8a93a8';
      ctx.fillStyle = '#171d2c'; rr(r.x, r.y, r.w, r.h, 9); ctx.fill();
      ctx.save(); rr(r.x, r.y, r.w, r.h, 9); ctx.clip();
      // 元素色微染 + 漸層頂帶
      const w2 = ctx.createLinearGradient(r.x, r.y, r.x, r.y + r.h);
      w2.addColorStop(0, this._rgba(accent, 0.30)); w2.addColorStop(1, this._rgba(accent, 0.04));
      ctx.fillStyle = w2; ctx.fillRect(r.x, r.y, r.w, r.h);
      const hb2 = ctx.createLinearGradient(r.x, r.y, r.x, r.y + 24);
      hb2.addColorStop(0, this._shade(accent, 45)); hb2.addColorStop(1, accent);
      ctx.fillStyle = hb2; ctx.fillRect(r.x, r.y, r.w, 24);
      ctx.restore();
      // 名稱（白字 + 深描邊，任何元素色都清楚）
      ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(8,12,22,0.85)';
      ctx.strokeText(def.name, r.x + r.w / 2, r.y + 12);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(def.name, r.x + r.w / 2, r.y + 12);
      this._cardMedallion(def, r.x + r.w / 2, r.y + 56, 18);
      ctx.fillStyle = '#b9c6dc'; ctx.font = '11px sans-serif'; ctx.textBaseline = 'alphabetic';
      ctx.fillText(`牌庫 ×${opts[i].count}`, r.x + r.w / 2, r.y + r.h - 9);
      ctx.strokeStyle = this._rgba(accent, 0.6); ctx.lineWidth = 1.5;
      rr(r.x, r.y, r.w, r.h, 9); ctx.stroke();
    }
    ctx.textBaseline = 'alphabetic';
  }

  private _cardSidePanels(g: Game): void {
    const deck = g.playerDeck;
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    // 牌庫 / 棄牌：可見卡背牌堆 + 數量徽記（抽牌動畫的實體起訖點）。
    const discTop = deck && deck.discardPile.length
      ? deck.discardPile[deck.discardPile.length - 1].cardId : undefined;
    this._drawPile(Renderer.deckPileRect(), '牌庫', deck ? deck.counts.draw : 0, false);
    this._drawPile(Renderer.discardPileRect(), '棄牌', deck ? deck.counts.discard : 0, true, discTop);
    // 手牌數（小字，牌堆下方）
    ctx.fillStyle = '#8aa0c0'; ctx.font = '12px sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    ctx.fillText(`手牌 ${deck ? deck.counts.hand : 0}`, 16, TOP_BAR_H + 112);
    // 投降按鈕（左欄下方）
    const sr = Renderer.cardSurrenderRect();
    ctx.fillStyle = 'rgba(80,30,38,0.7)'; ctx.fillRect(sr.x, sr.y, sr.w, sr.h);
    ctx.strokeStyle = '#c8546a'; ctx.lineWidth = 1.5; ctx.strokeRect(sr.x, sr.y, sr.w, sr.h);
    ctx.fillStyle = '#ff9aa8'; ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('投降', sr.x + sr.w / 2, sr.y + sr.h / 2);
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    // 右欄：調度中 / 目前佇列 / 出牌指引
    const rx = W - RIGHT_PANEL_W + 16, ry = TOP_BAR_H + 14;
    if (g.cycleMode) {
      ctx.fillStyle = '#7fe0ff'; ctx.font = 'bold 15px sans-serif';
      ctx.fillText('調度中', rx, ry);
      ctx.fillStyle = '#9fb2d0'; ctx.font = '12px sans-serif';
      ctx.fillText(`點手牌標記要棄的牌（至多 ${g.cycleLimit}）`, rx, ry + 24);
      ctx.fillText(`已標記 ${g.cycleMarks.length} 張`, rx, ry + 42);
      ctx.fillText('再點「調度」＝棄牌補抽、返回＝取消', rx, ry + 60);
    } else if (g.cardQueueUids.length > 0 && g.cardQueueEl) {
      const sp = SPELLS[ELEM[g.cardQueueEl]];
      const n = g.cardQueueUids.length;
      ctx.fillStyle = '#ffd166'; ctx.font = 'bold 15px sans-serif';
      ctx.fillText(`${sp ? sp.name : ''}${n > 1 ? ` ×${n}` : ''}`, rx, ry);
      ctx.fillStyle = '#9fb2d0'; ctx.font = '12px sans-serif';
      ctx.fillText('點戰場格放置（自動跳下個 tick）', rx, ry + 24);
      ctx.fillStyle = '#9be8a6';
      ctx.fillText('綠框手牌可連點疊加', rx, ry + 42);
    } else {
      ctx.fillStyle = '#9fb2d0'; ctx.font = '12px sans-serif';
      const lines = ['出牌流程（Tick 自動推進）：', '1. 點手牌（可連點同色疊加）', '2. 點戰場格放置', '→ 自動跳下一個 tick', '濾牌牌點了即時生效', '返回 / 跳過 在右上'];
      lines.forEach((t, i) => ctx.fillText(t, rx, ry + i * 20));
    }
    ctx.textBaseline = 'alphabetic';
  }

  /**
   * Session 10 follow-up — render the active buff/debuff/shield badges
   * for a unit. Sits above the HP bar; each modifier kind gets a small
   * colored circle with a 1-char glyph inside.
   *
   * Visual encoding:
   *   - buffAtk positive (Bless)         → gold "↑"
   *   - buffAtk negative (Whirlwind foe) → magenta "↓"
   *   - shieldPhys (Shield Wall phys)    → green  "P"
   *   - shieldMag  (Shield Wall mag)     → cyan   "M"
   *
   * Duration cue:
   *   - opacity = max(0.5, ticksRemaining / startingTicks) — slowly
   *     fades as the buff approaches expiry
   *   - on the LAST tick (ticksRemaining === 1) the badge also pulses
   *     in scale so the player notices "ends this round"
   *
   * @param u  unit whose modifiers to draw
   * @param px unit X center
   * @param hpBarY the Y of the HP bar (badges go just above it)
   */
  private _drawUnitModifierBadges(u: BattleUnit, px: number, hpBarY: number): void {
    if (!u.modifiers || u.modifiers.length === 0) return;
    const r = Math.max(4, CB * 0.10);
    const gap = r * 2 + 2;
    // Row is centered on px; total width = (n-1) * gap, leftmost center at px - (n-1)*gap/2.
    const n = u.modifiers.length;
    const baseX = px - ((n - 1) * gap) / 2;
    const cy = hpBarY - r - 3;
    const tNow = performance.now() / 1000;
    for (let i = 0; i < n; i++) {
      const m = u.modifiers[i];
      const cx = baseX + i * gap;

      // Color + glyph by kind (debuff = negative buffAtk).
      let color: string;
      let glyph: string;
      switch (m.kind) {
        case 'buffAtk':
          if (m.value >= 0) { color = '#ffcc22'; glyph = '↑'; }
          else              { color = '#ee44bb'; glyph = '↓'; }
          break;
        case 'shieldPhys': color = '#88ffbb'; glyph = 'P'; break;
        case 'shieldMag':  color = '#4499ff'; glyph = 'M'; break;
        // Session 11 (Leader CCC) — rooted: brown/olive badge with 'R'
        // glyph. Color picked to NOT collide with the existing badge
        // palette (yellow buff / magenta debuff / mint phys / blue mag).
        case 'rooted':     color = '#cc8844'; glyph = 'R'; break;
        // Session 11 follow-up — defDown (vulnerability): red-orange
        // badge with 'D' glyph. Same hue family as the floattext so
        // they read together. Distinct from rooted's brown.
        case 'defDown':    color = '#ff6633'; glyph = 'D'; break;
        default: continue;
      }

      // Fade opacity proportional to remaining/starting (floor 0.5).
      const ratio = m.startingTicks > 0
        ? Math.max(0, Math.min(1, m.ticksRemaining / m.startingTicks))
        : 1;
      const alpha = Math.max(0.5, ratio);
      // Pulse on last tick: scale oscillates 1.0 ↔ 1.18.
      const pulse = m.ticksRemaining === 1 ? (1 + 0.18 * Math.sin(tNow * 6)) : 1;
      const drawR = r * pulse;

      // Circle body
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(cx, cy, drawR, 0, Math.PI * 2);
      ctx.fill();
      // Thin dark rim for readability against pale unit body
      ctx.strokeStyle = 'rgba(0,0,0,0.55)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Glyph (black for high-contrast on bright fills)
      ctx.fillStyle = 'rgba(0,0,0,0.85)';
      ctx.font = `bold ${Math.max(7, drawR * 1.4)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(glyph, cx, cy + 0.5);
      ctx.restore();
    }
  }

  /**
   * Session 6c follow-up: sticky phase banner above the battlefield. Set
   * by Game._applyEvent at each EXEC sub-step (Friendly Spells / Move /
   * Combat / Hostile Spells / Contest) and stays put until replaced by
   * the next sub-step. No fade-out — clears between rounds.
   */
  private _phaseBanner(g: Game): void {
    if (!g.phaseBanner) return;
    ctx.fillStyle = g.phaseBanner.color;
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    ctx.fillText(g.phaseBanner.text, W / 2, OYB - 8);
  }

  private _gameOver(g: Game): void {
    // 置於畫面中下（不再貼底）；加半透明底板提升可讀性。
    const cy = Math.round(H * 0.56);
    // 底板須比 Play Again 按鈕(W*0.5)寬，否則按鈕戳出框外。
    const panelW = Math.round(W * 0.62), panelH = 186;
    ctx.save();
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = 'rgba(10,14,26,0.92)';
    this._rrPath(W / 2 - panelW / 2, cy - 70, panelW, panelH, 14); ctx.fill();
    ctx.strokeStyle = 'rgba(90,120,180,0.5)'; ctx.lineWidth = 1.5;
    this._rrPath(W / 2 - panelW / 2, cy - 70, panelW, panelH, 14); ctx.stroke();
    ctx.restore();
    // Phase 3d Step 4 fix: VICTORY/DEFEAT relative to local player's team.
    const myTeam = g.myTeam;
    const oppTeam = (myTeam === 0 ? 1 : 0) as 0 | 1;
    const won = g.winner === myTeam;
    const lost = g.winner === oppTeam;
    ctx.fillStyle = won ? '#2255aa' : lost ? '#aa2233' : '#555';
    ctx.font = 'bold 28px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(won ? 'VICTORY!' : lost ? 'DEFEAT' : 'DRAW', W / 2, cy - 40);
    // Phase 3d Step 5: explain the result if opponent dropped out mid-battle.
    // selfDisconnected mirrors this for the side whose own socket dropped —
    // shows DEFEAT + "(連線中斷)" instead of leaving them stuck behind the
    // "Waiting for opponent" overlay.
    if (g.opponentLeft) {
      ctx.fillStyle = '#ffaa44';
      ctx.font = '14px sans-serif';
      ctx.fillText('(Opponent disconnected)', W / 2, cy - 18);
    } else if (g.selfDisconnected) {
      ctx.fillStyle = '#ffaa44';
      ctx.font = '14px sans-serif';
      ctx.fillText('(連線中斷)', W / 2, cy - 18);
    }
    ctx.fillStyle = '#aaa'; ctx.font = '14px sans-serif';
    const fhpMine = g._teamHP(myTeam), fhpOpp = g._teamHP(oppTeam);
    const bMine = g.bases.find(b => b.team === myTeam);
    const bOpp = g.bases.find(b => b.team === oppTeam);
    const opponentLabel = g.gameMode === 'single' ? 'AI' : 'Opp';
    ctx.fillText(`You: ${fhpMine.healthy} soldiers | ${opponentLabel}: ${fhpOpp.healthy} soldiers`, W / 2, cy - 10);
    // Base status
    ctx.font = '12px sans-serif'; ctx.fillStyle = '#888';
    const bMineStatus = bMine ? (bMine.destroyed ? 'DESTROYED' : `${bMine.hp}/${bMine.maxHp} HP`) : '—';
    const bOppStatus = bOpp ? (bOpp.destroyed ? 'DESTROYED' : `${bOpp.hp}/${bOpp.maxHp} HP`) : '—';
    ctx.fillText(`Your Base: ${bMineStatus} | ${opponentLabel} Base: ${bOppStatus}`, W / 2, cy + 8);
    // Play Again button → restart to MENU.
    ctx.fillStyle = '#1a2545'; ctx.fillRect(W * 0.25, cy + 30, W * 0.5, 44);
    ctx.strokeStyle = '#4488ff'; ctx.lineWidth = 2; ctx.strokeRect(W * 0.25, cy + 30, W * 0.5, 44);
    ctx.fillStyle = '#88bbff'; ctx.font = 'bold 16px sans-serif';
    ctx.fillText('Play Again', W / 2, cy + 55);
  }

  private _fx(g: Game): void {
    for (const f of g.fx) {
      ctx.globalAlpha = clamp(f.alpha, 0, 1);

      if (f.type === 'floatText') {
        ctx.fillStyle = f.color; ctx.font = `bold ${f.size || 14}px sans-serif`;
        ctx.textAlign = 'center'; ctx.fillText(f.text!, f.x!, f.y!);
      }

      if (f.type === 'm3Score') {
        // "+N" point gain text travelling from gem centre toward HUD bar.
        // Game.update tweens (x,y) toward (x2,y2); we draw with a soft
        // outline to keep readability over the busy gem grid.
        const fs = f.size || 14;
        ctx.font = `bold ${fs}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.strokeText(f.text!, f.x!, f.y!);
        ctx.fillStyle = f.color;
        ctx.fillText(f.text!, f.x!, f.y!);
      }

      if (f.type === 'comboBanner') {
        // Combo banner: large bold text scaled by f.scale, with soft halo.
        const baseSize = f.size || 18;
        const sc = f.scale || 1;
        ctx.font = `bold ${Math.round(baseSize * sc)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.save();
        ctx.shadowColor = f.color;
        ctx.shadowBlur = 14;
        // Double-stroke for chunky readability over the gem board.
        ctx.lineWidth = 5;
        ctx.strokeStyle = 'rgba(0,0,0,0.78)';
        ctx.strokeText(f.text!, f.x!, f.y!);
        ctx.fillStyle = f.color;
        ctx.fillText(f.text!, f.x!, f.y!);
        ctx.restore();
      }

      if (f.type === 'flash') {
        // Full-screen colour overlay — drawn last (FX render order) so it
        // tints everything underneath. globalAlpha is already set above
        // to clamp(f.alpha, 0, 1), so we just fill the whole canvas.
        ctx.fillStyle = f.color;
        ctx.fillRect(0, 0, W, H);
      }

      if (f.type === 'ring') {
        ctx.strokeStyle = f.color; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.arc(f.x!, f.y!, Math.max(0, f.radius!), 0, Math.PI * 2); ctx.stroke();
      }

      if (f.type === 'slash') {
        ctx.strokeStyle = f.color; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(f.x1!, f.y1!); ctx.lineTo(f.x2!, f.y2!); ctx.stroke();
        // Arrow head
        const angle = Math.atan2(f.y2! - f.y1!, f.x2! - f.x1!);
        ctx.beginPath();
        ctx.moveTo(f.x2!, f.y2!);
        ctx.lineTo(f.x2! - 10 * Math.cos(angle - 0.35), f.y2! - 10 * Math.sin(angle - 0.35));
        ctx.moveTo(f.x2!, f.y2!);
        ctx.lineTo(f.x2! - 10 * Math.cos(angle + 0.35), f.y2! - 10 * Math.sin(angle + 0.35));
        ctx.stroke();
      }

      if (f.type === 'moveTrail') {
        ctx.strokeStyle = f.color; ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(f.x1!, f.y1!); ctx.lineTo(f.x2!, f.y2!); ctx.stroke();
        ctx.setLineDash([]);
      }

      if (f.type === 'moveArrow') {
        // Solid arrow showing movement direction
        const angle = Math.atan2(f.y2! - f.y1!, f.x2! - f.x1!);
        const sz = 6;
        ctx.fillStyle = f.color;
        ctx.beginPath();
        ctx.moveTo(f.x2! + sz * Math.cos(angle), f.y2! + sz * Math.sin(angle));
        ctx.lineTo(f.x2! - sz * Math.cos(angle - 0.6), f.y2! - sz * Math.sin(angle - 0.6));
        ctx.lineTo(f.x2! - sz * Math.cos(angle + 0.6), f.y2! - sz * Math.sin(angle + 0.6));
        ctx.closePath(); ctx.fill();
      }

      if (f.type === 'particle') {
        ctx.fillStyle = f.color;
        const ps = f.size || 3;
        ctx.beginPath(); ctx.arc(f.x!, f.y!, ps, 0, Math.PI * 2); ctx.fill();
      }

      if (f.type === 'deathX') {
        // Draw an X mark where unit died
        const s = (f.size || 10) * (f.scale || 1);
        ctx.strokeStyle = f.color; ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(f.x! - s, f.y! - s); ctx.lineTo(f.x! + s, f.y! + s);
        ctx.moveTo(f.x! + s, f.y! - s); ctx.lineTo(f.x! - s, f.y! + s);
        ctx.stroke();
      }

      ctx.globalAlpha = 1;
    }
  }

  private _transition(g: Game): void {
    if (g.transitionTimer <= 0) return;
    // Fade-in banner across the middle of the screen. Stays at full alpha
    // for the bulk of the timer, fades out in the last 0.35s. Works for
    // both the short (0.6s) round-start banner AND the longer (1.4s)
    // MATCH3→SCHED entrance banner (Session 6c follow-up).
    const t = g.transitionTimer;
    const fadeWindow = 0.35;
    const alpha = t > fadeWindow ? 1 : Math.max(0, t / fadeWindow);
    ctx.globalAlpha = alpha * 0.65;
    ctx.fillStyle = '#000';
    const bh = 56;
    ctx.fillRect(0, H / 2 - bh / 2, W, bh);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#ffdd44';
    ctx.font = 'bold 22px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(g.transitionLabel, W / 2, H / 2);
    ctx.globalAlpha = 1;
  }

  /**
   * Session 6c follow-up — loading spinner shown while the client is
   * still talking to the server (initBattle in flight, or PvP lobby
   * waiting for opponent's initBattle to land). Sits over whatever
   * screen is currently visible (DEPLOY / LOBBY) so the player knows
   * the app isn't frozen. Spinning ring + label + animated dots.
   */
  private _loadingOverlay(g: Game): void {
    // Dim everything behind us.
    ctx.fillStyle = 'rgba(8,12,28,0.78)';
    ctx.fillRect(0, 0, W, H);

    const cx = W / 2;
    const cy = H / 2;
    const radius = 22;

    // Spinner ring.
    const t = Date.now() / 1000;
    ctx.strokeStyle = 'rgba(80,140,220,0.25)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(cx, cy - 16, radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = '#88bbff';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    const start = (t * 2.4) % (Math.PI * 2);
    ctx.beginPath();
    ctx.arc(cx, cy - 16, radius, start, start + Math.PI * 1.2);
    ctx.stroke();
    ctx.lineCap = 'butt';

    // Label.
    const dots = '.'.repeat((((Date.now() / 350) | 0) % 4));
    const label = (g as Game & { loadingLabel?: string }).loadingLabel ?? 'Connecting to server';
    ctx.fillStyle = '#cce0ff';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label + dots, cx, cy + 24);
  }
}
